import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import {
  TestTarget,
  ItemDetails,
  UseCase,
  ReviewStatus,
  ViewPath,
  Policy,
} from "../types/TestTarget";
import { SSTFApiClient, TestTargetAutomationStats } from "../services/api";
import { fetchUsecases, UsecaseView } from "../services/procedureClient";
import {
  saveFeatureFromUseCase,
  SaveFeatureExisting,
} from "../services/featureGenerator";
import { UseCaseEditModal } from "./UseCaseEdit/UseCaseEditModal";
import { ApiConfig } from "../config/apiConfig";
import { as_item_details } from "../utils/itemParams";
import { useToast } from "../contexts/ToastContext";
import {
  STATUS_LABELS,
  getStatus,
  isPendingReview,
} from "../utils/statusUtils";
import { OperationLogImportDialog } from "./OperationLogImportDialog";
import { TestTargetRegistrationDialog } from "./TestTargetRegistrationDialog";
import {
  getViewPaths,
  setViewPaths,
  normalizeViewPaths,
  getParamsConditions,
  getViewComponentConditions,
  getConditionsForComponentId,
  upsertComponentCondition,
  removeComponentCondition,
  swapComponentIdPreservingConditions,
  normalizeComponentConditionsForSave,
} from "../utils/viewPathUtils";
import type { ComponentConditionEntry } from "../types/TestTarget";
import {
  ConditionsBlock,
  ConditionsBlockLabel,
  ConditionsReadOnlyList,
  ConditionsReadOnlyItem,
} from "./componentConditionsStyles";
import { FIELD_HELP, FieldHelpButton } from "./help/fieldHelp";
import { find_parent_view_id } from "../utils/testTargetHierarchy";
import {
  OperationLogService,
  TestTarget as OperationLogTestTarget,
  GenerateFromOperationLogResponse,
  PageResult,
} from "../services/operationLogService";
import {
  ListAlt as ListAltIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  PlayArrow as PlayArrowIcon,
  Folder as FolderIcon,
  Article as ArticleIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Visibility as VisibilityIcon,
  Dashboard as DashboardIcon,
  Layers as LayersIcon,
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { CircularProgress, Tooltip } from "@mui/material";
import { formatMaskTypeForList } from "../utils/maskSettings";
import {
  build_component_table_display_rows,
  build_component_ref_child_rows,
  build_view_render_rows,
  get_component_ids_from_view_params,
  get_components_under_service,
  resolve_component_by_stored_id,
  resolve_component_ref_target,
  unique_trimmed_component_ids,
  type ComponentTableDisplayRow,
  type ViewItemPanelRow as PanelItemRow,
} from "../utils/viewItemTree";

// Sidebar Panel Container
const PanelContainer = styled.div<{
  isOpen: boolean;
  width: number;
  isResizing: boolean;
}>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: ${(props) => `${props.width}px`};
  background: white;
  transition: ${(props) => (props.isResizing ? "none" : "none")};
  overflow: hidden;
  user-select: ${(props) => (props.isResizing ? "none" : "auto")};
  max-height: 100%;
  border-left: 1px solid #e2e8f0;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.08);
`;

// Resize Handle
const ResizeHandle = styled.div<{ isResizing: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: ${(props) => (props.isResizing ? "#667eea" : "transparent")};
  cursor: col-resize;
  z-index: 1300;
  transition: background 0.2s ease;

  &:hover {
    background: #667eea;
  }

  &::before {
    content: "";
    position: absolute;
    left: -4px;
    top: 0;
    width: 12px;
    height: 100%;
    background: transparent;
  }
`;

// Panel Content
const PanelContent = styled.div<{ width: number }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

// Panel Header
const PanelHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 72px;
  
  .header-icon {
    color: #667eea;
    display: flex;
    align-items: center;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TestCaseCountBadge = styled.span`
  font-size: 0.7rem;
  color: #667eea;
  background: rgba(102, 126, 234, 0.1);
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: 600;
  border: 1px solid rgba(102, 126, 234, 0.2);
  white-space: nowrap;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 8px;

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

// Panel Body
const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px;
  
  /* 統一されたスクロールバーのスタイリング */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(148, 163, 184, 0.5);
    }
  }
`;

// Panel Footer
const PanelFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: white;
  display: flex;
  gap: 12px;
  justify-content: flex-start;
`;

// Button Components
const Button = styled.button<{ variant?: "primary" | "secondary" | "outline" }>`
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: #667eea;
          color: white;
          &:hover:not(:disabled) {
            background: #5a67d8;
          }
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `;
      case "secondary":
        return `
          background: white;
          color: #667eea;
          border: 1.5px solid #cbd5e1;
          &:hover:not(:disabled) {
            background: #f8fafc;
            border-color: #667eea;
          }
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background: #f8fafc;
          }
        `;
      case "outline":
        return `
          background: transparent;
          color: #667eea;
          border: 1px solid #667eea;
          &:hover:not(:disabled) {
            background: #f1f5f9;
          }
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `;
      default:
        return `
          background: #f1f5f9;
          color: #475569;
          &:hover:not(:disabled) {
            background: #e2e8f0;
          }
          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `;
    }
  }}
`;

const IconButton = styled.button<{ active?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: ${(props) => (props.active ? "#667eea" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#64748b")};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.active ? "#5a67d8" : "#667eea")};
    color: #fff;
  }
`;

const Chip = styled.span<{
  variant?: "primary" | "error" | "outline";
  backgroundColor?: string;
}>`
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;

  ${(props) => {
    if (props.backgroundColor) {
      return `
        background: ${props.backgroundColor};
        color: white;
      `;
    }
    switch (props.variant) {
      case "primary":
        return `
          background: #e1effe;
          color: #667eea;
          border: 1px solid #667eea;
        `;
      case "error":
        return `
          background: #ef4444;
          color: white;
        `;
      case "outline":
        return `
          background: #e2e8f0;
          color: #475569;
          border: 1px solid #e2e8f0;
        `;
      default:
        return `
          background: #e2e8f0;
          color: #475569;
        `;
    }
  }}
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(148, 163, 184, 0.5);
    }
  }

`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  table-layout: fixed;
`;

const TableHead = styled.thead`
  background: #f8fafc;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.15s ease;

  &:nth-child(even) {
    background: #f8fafc;
  }

  &:hover {
    background: #e2e8f0 !important;
  }
`;

const TableCell = styled.td<{ width?: string }>`
  padding: 4px;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.65rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;
  width: ${(props) => props.width || "auto"};
  max-width: 0;
  overflow: hidden;
`;

// 階層表示用のコンテナ
const HierarchyContainer = styled.div<{ level: number }>`
  display: flex;
  align-items: center;
  position: relative;
  padding-left: ${(props) => props.level * 16}px;
  
  /* 縦線（親から子への接続線） */
  &::before {
    content: '';
    position: absolute;
    left: ${(props) => (props.level - 1) * 16 + 8}px;
    top: -8px;
    bottom: 50%;
    width: 1px;
    background: #cbd5e1;
    display: ${(props) => (props.level > 0 ? "block" : "none")};
  }
  
  /* 横線（子アイテムへの接続線） */
  &::after {
    content: '';
    position: absolute;
    left: ${(props) => (props.level - 1) * 16 + 8}px;
    top: 50%;
    width: 8px;
    height: 1px;
    background: #cbd5e1;
    display: ${(props) => (props.level > 0 ? "block" : "none")};
  }
`;

const ItemIcon = styled.div<{ isParent: boolean }>`
  display: flex;
  align-items: center;
  margin-right: 6px;
  color: ${(props) => (props.isParent ? "#667eea" : "#94a3b8")};
  font-size: 14px;
  z-index: 1;
  background: white;
  padding: 2px;
`;

const ItemNameContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TableHeaderCell = styled.th<{ width?: string }>`
  padding: 4px;
  border-bottom: 2px solid #e2e8f0;
  text-align: left;
  font-weight: 600;
  font-size: 0.65rem;
  color: #475569;
  width: ${(props) => props.width || "auto"};
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.2;
  position: relative;
`;

// 列幅リサイズハンドル
const ColumnResizeHandle = styled.div<{ isResizing: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: ${(props) => (props.isResizing ? "#667eea" : "transparent")};
  cursor: col-resize;
  z-index: 10;
  user-select: none;

  &:hover {
    background: #667eea;
  }

  &::before {
    content: "";
    position: absolute;
    right: -2px;
    top: 0;
    width: 8px;
    height: 100%;
    background: transparent;
  }
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  background: #f8fafc;
  border-radius: 8px;
  border: 2px dashed #e2e8f0;
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  color: #64748b;
  font-size: 1rem;
`;

const EmptyText = styled.p`
  margin: 0 0 16px 0;
  color: #64748b;
  font-size: 0.85rem;
`;

const RequiredIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ef4444;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::after {
    content: '';
    width: 6px;
    height: 6px;
    background: white;
    border-radius: 50%;
    position: absolute;
  }
`;

const OptionalIndicator = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  background: transparent;
  display: inline-flex;
`;

// Styled icon components
const StyledListIcon = styled(ListAltIcon)`
  font-size: 1.1rem;
  color: #667eea;
`;

const StyledEditIcon = styled(EditIcon)`
  font-size: 1rem;
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  font-size: 1rem;
`;

const StyledAddIcon = styled(AddIcon)`
  font-size: 1rem;
`;

const StyledRequiredIcon = styled(CheckCircleIcon)`
  font-size: 0.8rem;
  color: #ef4444;
`;

const StyledOptionalIcon = styled(RadioButtonUncheckedIcon)`
  font-size: 0.8rem;
  color: #64748b;
`;

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 1rem;
`;

const StyledTrendingUpIcon = styled(TrendingUpIcon)`
  font-size: 0.7rem;
  color: #10b981;
`;

const StyledTrendingDownIcon = styled(TrendingDownIcon)`
  font-size: 0.7rem;
  color: #ef4444;
`;

const StyledPlayIcon = styled(PlayArrowIcon)`
  font-size: 0.7rem;
  color: #667eea;
`;

// ステータスバッジ
const StatusBadge = styled.span<{ status: ReviewStatus }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;
  gap: 4px;
  white-space: nowrap;

  ${(props) => {
    switch (props.status) {
      case "draft":
        return `
          background: #f3f4f6;
          color: #6b7280;
        `;
      case "need_review":
        return `
          background: #fef3c7;
          color: #d97706;
          border: 1px solid #fbbf24;
        `;
      case "in_review":
        return `
          background: #dbeafe;
          color: #2563eb;
          border: 1px solid #60a5fa;
        `;
      case "approved":
        return `
          background: #d1fae5;
          color: #059669;
          border: 1px solid #34d399;
        `;
      case "rejected":
        return `
          background: #fee2e2;
          color: #dc2626;
          border: 1px solid #f87171;
        `;
      case "archived":
        return `
          background: #e5e7eb;
          color: #4b5563;
        `;
      default:
        return `
          background: #f3f4f6;
          color: #6b7280;
        `;
    }
  }}
`;

// セレクタなしタグ
const NoSelectorBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
  white-space: nowrap;
  gap: 4px;
`;

// 手動実行モードタグ
const ManualOperationBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 500;
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #f87171;
  white-space: nowrap;
  gap: 4px;
`;

const StyledFolderIcon = styled(FolderIcon)`
  font-size: inherit;
`;

const StyledArticleIcon = styled(ArticleIcon)`
  font-size: inherit;
`;

const StyledDashboardIcon = styled(DashboardIcon)`
  font-size: inherit;
`;

const StyledLayersIcon = styled(LayersIcon)`
  font-size: inherit;
`;

// アイコン定義
const Icons = {
  List: <StyledListIcon />,
  Edit: <StyledEditIcon />,
  Delete: <StyledDeleteIcon />,
  Add: <StyledAddIcon />,
  Required: <StyledRequiredIcon />,
  Optional: <StyledOptionalIcon />,
  Close: <StyledCloseIcon />,
  TrendingUp: <StyledTrendingUpIcon />,
  TrendingDown: <StyledTrendingDownIcon />,
  Play: <StyledPlayIcon />,
};

// セレクタ存在判定関数
// セレクタが存在しない、または空であるかを判定する
// 複数のセレクタが設定されている場合、すべてのセレクタが有効（空でない）でない限りfalseを返す
const hasSelector = (item: TestTarget): boolean => {
  if (!item.params || typeof item.params !== "object") return false;
  if (!("selector" in item.params)) return false;
  
  const selector = (item.params as ItemDetails).selector;
  if (!selector) return false;
  
  if (typeof selector === "string") {
    return selector.trim().length > 0;
  }
  
  if (typeof selector === "object" && !Array.isArray(selector)) {
    const values = Object.values(selector);
    // すべてのセレクタ値が有効（空でない文字列）である必要がある
    return values.length > 0 && values.every(v => v && typeof v === "string" && v.trim().length > 0);
  }
  
  return false;
};

// 手動実行モード判定関数
// paramsにis_manual: trueがあるかを判定する
const isManualOperation = (item: TestTarget): boolean => {
  if (!item.params || typeof item.params !== "object") return false;
  
  const details = item.params as ItemDetails;
  // isManualプロパティをチェック（APIではis_manualとして保存される可能性もあるため両方をチェック）
  if ("isManual" in details && details.isManual === true) return true;
  if ("is_manual" in details && (details as any).is_manual === true) return true;
  
  return false;
};

// タブ関連のスタイル
const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TabList = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 6px 6px 0 0;
  padding: 0 4px;
`;

const TabButton = styled.button<{ $isActive: boolean }>`
  padding: 12px 16px;
  border: none;
  background: ${(props) => (props.$isActive ? "white" : "transparent")};
  color: ${(props) => (props.$isActive ? "#1e293b" : "#64748b")};
  font-size: 0.8rem;
  font-weight: ${(props) => (props.$isActive ? "600" : "500")};
  cursor: pointer;
  border-radius: 4px 4px 0 0;
  border-bottom: ${(props) =>
    props.$isActive ? "2px solid #667eea" : "2px solid transparent"};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: ${(props) => (props.$isActive ? "white" : "#f1f5f9")};
    color: ${(props) => (props.$isActive ? "#1e293b" : "#475569")};
  }
`;

const TabContent = styled.div`
  flex: 1;
  min-height: 0;
  background: white;
  border-radius: 0 0 6px 6px;
  border: 1px solid #e2e8f0;
  border-top: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TabBody = styled.div`
  flex: 1;
  min-height: 0;
  padding: 20px;
  overflow-y: auto;
`;

const TabFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 0 0 6px 6px;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
`;

const BulkDeleteButton = styled(Button)`
  background: #ef4444;
  color: #ffffff;
  margin-left: auto;

  &:hover:not(:disabled) {
    background: #e92929;
  }

  &:disabled {
    background: #fecdd3;
    color: #ffffff;
    cursor: not-allowed;
  }
`;

// UseCase表示用コンポーネント
const UseCaseSection = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

// #884: 機能タブのツールバー（機能を作成 ボタン）
const FeatureToolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const UseCaseSectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UseCaseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const UseCaseItem = styled.div`
  padding: 6px 10px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  font-size: 0.65rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  min-height: 32px;
  
  &:hover {
    border-color: #667eea;
    background: #f8fafc;
    box-shadow: 0 1px 3px rgba(102, 126, 234, 0.1);
  }
`;

const UseCaseContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const UseCaseTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
`;

const UseCaseTitle = styled.div`
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.7rem;
  flex: 1;
`;

const UseCaseDescription = styled.div`
  font-weight: 400;
  color: #64748b;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.6rem;
`;

const UseCaseIdText = styled.div`
  font-size: 0.7rem;
  color: #1f2937;
  margin-bottom: 4px;
  word-break: break-all;
`;

const UseCaseSteps = styled.div`
  font-size: 0.65rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StepIndicator = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  background: #667eea;
  color: white;
  border-radius: 50%;
  font-size: 0.6rem;
  font-weight: 600;
  text-align: center;
  line-height: 16px;
`;

const UseCaseCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #ef4444;
  cursor: pointer;
`;

// ビュー情報表示・編集用のスタイル
const ViewInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px 0;
`;

const ViewInfoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ViewInfoLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`;

const ViewInfoInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ViewInfoTextarea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  min-height: 200px;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ViewInfoValue = styled.div`
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #1f2937;
  border: 1px solid #e2e8f0;
  min-height: 40px;
  display: flex;
  align-items: flex-start;
  white-space: pre-wrap;
  line-height: 1.5;
`;

const ViewInfoCheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
`;

const ViewInfoCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #667eea;
`;

const ViewInfoCheckboxLabel = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ViewInfoDescription = styled.div`
  font-size: 0.8rem;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.4;
`;

// パス入力用のスタイル
const PathInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PathInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const PathInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const PathList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PathItem = styled.div`
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #1f2937;
  border: 1px solid #e2e8f0;
`;

const PathSelect = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const ComponentErrorText = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: #b91c1c;
  margin-top: 2px;
  margin-bottom: 4px;
`;

/** 項目仕様書パネルで表示する対象種別の短い名称（タブ・ラベル・トースト等） */
function spec_panel_target_kind_label(
  target: TestTarget | null | undefined
): "ビュー" | "コンポーネント" {
  return target?.type === "component" ? "コンポーネント" : "ビュー";
}

function find_root_service_id(
  targetId: string | undefined,
  targets: TestTarget[]
): string | null {
  if (!targetId) {
    return null;
  }
  let current: TestTarget | undefined = targets.find((x) => x.id === targetId);
  while (current) {
    if (current.type === "service") {
      return current.id;
    }
    const parentId = current.parentId;
    if (!parentId) {
      return null;
    }
    current = targets.find((x) => x.id === parentId);
  }
  return null;
}

// ビュー表示・編集コンポーネント
const ViewDisplay: React.FC<{
  viewTarget: TestTarget | null;
  isEditing: boolean;
  isSaving?: boolean;
  allTestTargets: TestTarget[];
  editedData: {
    name: string;
    description: string;
    paths: ViewPath[];
    conditions: string[];
    component_ids: string[];
    entry_point: boolean;
    status?: ReviewStatus;
    auto_upload_count?: number;
    componentConditions: ComponentConditionEntry[];
  };
  onDataChange: (data: {
    name: string;
    description: string;
    paths: ViewPath[];
    conditions: string[];
    component_ids: string[];
    entry_point: boolean;
    status?: ReviewStatus;
    auto_upload_count?: number;
    componentConditions: ComponentConditionEntry[];
  }) => void;
}> = ({
  viewTarget,
  isEditing,
  isSaving = false,
  allTestTargets,
  editedData,
  onDataChange,
}) => {
  const target_kind_label = spec_panel_target_kind_label(viewTarget);
  const is_component_target = viewTarget?.type === "component";

  const serviceId = useMemo(
    () => find_root_service_id(viewTarget?.id, allTestTargets),
    [viewTarget?.id, allTestTargets]
  );

  const availableComponents = useMemo(() => {
    if (!serviceId) {
      return [] as { id: string; name: string }[];
    }
    return get_components_under_service(serviceId, allTestTargets)
      .slice()
      .sort(
        (a, b) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) ||
          a.name.localeCompare(b.name)
      )
      .map((t) => ({ id: t.id, name: t.name }));
  }, [serviceId, allTestTargets]);

  const getConditionsForId = (componentId: string): string[] =>
    getConditionsForComponentId(
      editedData.componentConditions,
      componentId
    );

  const writeConditionsForId = (
    componentId: string,
    nextConditions: string[]
  ): void => {
    onDataChange({
      ...editedData,
      componentConditions: upsertComponentCondition(
        editedData.componentConditions,
        componentId,
        nextConditions
      ),
    });
  };

  const handleComponentIdChange = (
    oldId: string,
    newId: string,
    index: number
  ): void => {
    const nextIds = [...editedData.component_ids];
    nextIds[index] = newId;
    onDataChange({
      ...editedData,
      component_ids: nextIds,
      componentConditions: swapComponentIdPreservingConditions(
        editedData.componentConditions,
        oldId,
        newId
      ),
    });
  };

  const removeComponentIdRow = (index: number): void => {
    const removed = editedData.component_ids[index] ?? "";
    const nextIds = editedData.component_ids.filter((_, i) => i !== index);
    const normalizedIds = nextIds.length > 0 ? nextIds : [""];
    onDataChange({
      ...editedData,
      component_ids: normalizedIds,
      componentConditions: removeComponentCondition(
        editedData.componentConditions,
        removed
      ),
    });
  };

  if (!viewTarget) {
    return (
      <EmptyState>
        <EmptyTitle>{target_kind_label}が選択されていません</EmptyTitle>
        <EmptyText>{target_kind_label}を選択してください</EmptyText>
      </EmptyState>
    );
  }

  return (
    <ViewInfoContainer>
      <ViewInfoGroup>
        <ViewInfoLabel>ID: {viewTarget.id}</ViewInfoLabel>
      </ViewInfoGroup>

      <ViewInfoGroup>
        <ViewInfoLabel>{target_kind_label}名</ViewInfoLabel>
        {isEditing ? (
          <ViewInfoInput
            value={editedData.name}
            onChange={(e) =>
              onDataChange({ ...editedData, name: e.target.value })
            }
            placeholder={`${target_kind_label}名を入力してください`}
          />
        ) : (
          <ViewInfoValue>{editedData.name || viewTarget.name}</ViewInfoValue>
        )}
      </ViewInfoGroup>

      {!is_component_target ? (
      <ViewInfoGroup>
        <ViewInfoLabel>パス</ViewInfoLabel>
        {isEditing ? (
          <PathInputContainer>
            {editedData.paths.map((pathObj, index) => (
              <PathInputRow key={index}>
                <PathInput
                  value={pathObj.path || ""}
                  onChange={(e) => {
                    const newPaths = [...editedData.paths];
                    newPaths[index] = { ...pathObj, path: e.target.value };
                    onDataChange({ ...editedData, paths: newPaths });
                  }}
                  placeholder="URLパスを入力してください（例: /login, /dashboard）"
                />
                <IconButton
                  onClick={() => {
                    const newPaths = editedData.paths.filter((_, i) => i !== index);
                    if (newPaths.length === 0) {
                      newPaths.push({ path: "" });
                    }
                    onDataChange({ ...editedData, paths: newPaths });
                  }}
                  title="パスを削除"
                  style={{ width: "32px", height: "32px" }}
                >
                  {Icons.Delete}
                </IconButton>
              </PathInputRow>
            ))}
            <Button
              variant="outline"
              onClick={() => {
                onDataChange({
                  ...editedData,
                  paths: [...editedData.paths, { path: "" }],
                });
              }}
              style={{ marginTop: "8px" }}
            >
              {Icons.Add} パスを追加
            </Button>
          </PathInputContainer>
        ) : (
          <ViewInfoValue>
            {editedData.paths.length > 0 && editedData.paths.some((p) => p.path) ? (
              <PathList>
                {editedData.paths
                  .filter((p) => p.path)
                  .map((pathObj, index) => (
                    <PathItem key={index}>{pathObj.path}</PathItem>
                  ))}
              </PathList>
            ) : (
              "パスが設定されていません"
            )}
          </ViewInfoValue>
        )}
      </ViewInfoGroup>
      ) : null}

      {!is_component_target ? (
      <ViewInfoGroup>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <ViewInfoLabel style={{ marginBottom: 0 }}>
            画面の表示条件
          </ViewInfoLabel>
          <FieldHelpButton
            help={FIELD_HELP.viewDisplayConditions}
            ariaLabel="画面の表示条件の説明"
          />
        </div>
        {isEditing ? (
          <PathInputContainer>
            {editedData.conditions.map((condition, index) => (
              <PathInputRow key={index}>
                <PathInput
                  value={condition}
                  onChange={(e) => {
                    const nextConditions = editedData.conditions.map((c, i) =>
                      i === index ? e.target.value : c
                    );
                    onDataChange({ ...editedData, conditions: nextConditions });
                  }}
                  placeholder="例: ログイン状態の場合 / 管理者ロールの場合 / 初回ログイン時のみ / 広告表示対象の場合"
                />
                <IconButton
                  onClick={() => {
                    const nextConditions = editedData.conditions.filter(
                      (_, i) => i !== index
                    );
                    onDataChange({ ...editedData, conditions: nextConditions });
                  }}
                  title="条件を削除"
                  style={{ width: "32px", height: "32px" }}
                >
                  {Icons.Delete}
                </IconButton>
              </PathInputRow>
            ))}
            <Button
              variant="outline"
              onClick={() =>
                onDataChange({
                  ...editedData,
                  conditions: [...editedData.conditions, ""],
                })
              }
              style={{ marginTop: "8px" }}
            >
              {Icons.Add} 条件を追加
            </Button>
          </PathInputContainer>
        ) : (
          <ViewInfoValue>
            {(() => {
              const conditions = editedData.conditions.filter(
                (c) => c.trim() !== ""
              );
              return conditions.length > 0 ? (
                <PathList>
                  {conditions.map((condition, index) => (
                    <PathItem key={index}>{condition}</PathItem>
                  ))}
                </PathList>
              ) : (
                "表示条件が設定されていません"
              );
            })()}
          </ViewInfoValue>
        )}
      </ViewInfoGroup>
      ) : null}

      {!is_component_target ? (
      <ViewInfoGroup>
        <ViewInfoLabel>共通コンポーネント</ViewInfoLabel>
        {isEditing ? (
          (() => {
            const hasAnyStoredId = editedData.component_ids.some(
              (id) => id.trim().length > 0
            );
            if (availableComponents.length === 0 && !hasAnyStoredId) {
              return (
                <ViewInfoValue style={{ color: "#64748b" }}>
                  共通コンポーネントが存在しません
                </ViewInfoValue>
              );
            }
            if (availableComponents.length === 0 && hasAnyStoredId) {
              return (
                <PathInputContainer>
                  {editedData.component_ids.map((cid, index) => {
                    const trimmed_cid = cid.trim();
                    const currentConditions = getConditionsForId(trimmed_cid);
                    return (
                      <div key={`orphan-${index}`}>
                        <PathInputRow>
                          <ViewInfoValue style={{ flex: 1, margin: 0 }}>
                            {trimmed_cid || "（空）"}
                          </ViewInfoValue>
                          <IconButton
                            onClick={() => removeComponentIdRow(index)}
                            title="この行を削除"
                            style={{ width: "32px", height: "32px" }}
                          >
                            {Icons.Delete}
                          </IconButton>
                        </PathInputRow>
                        <ComponentErrorText>
                          存在しない共通コンポーネントです
                        </ComponentErrorText>
                        {trimmed_cid ? (
                          <ConditionsBlock>
                            <ConditionsBlockLabel>
                              表示条件
                            </ConditionsBlockLabel>
                            {currentConditions.map((cond, ci) => (
                              <PathInputRow key={`orphan-cond-${index}-${ci}`}>
                                <PathInput
                                  value={cond}
                                  onChange={(e) => {
                                    const next = currentConditions.map(
                                      (c, i) =>
                                        i === ci ? e.target.value : c
                                    );
                                    writeConditionsForId(trimmed_cid, next);
                                  }}
                                  placeholder="例: プレミアム会員でログイン中"
                                />
                                <IconButton
                                  onClick={() =>
                                    writeConditionsForId(
                                      trimmed_cid,
                                      currentConditions.filter(
                                        (_, i) => i !== ci
                                      )
                                    )
                                  }
                                  title="条件を削除"
                                  style={{ width: "32px", height: "32px" }}
                                >
                                  {Icons.Delete}
                                </IconButton>
                              </PathInputRow>
                            ))}
                            <Button
                              variant="outline"
                              onClick={() =>
                                writeConditionsForId(trimmed_cid, [
                                  ...currentConditions,
                                  "",
                                ])
                              }
                              style={{ marginTop: "4px" }}
                            >
                              {Icons.Add} 条件を追加
                            </Button>
                          </ConditionsBlock>
                        ) : null}
                      </div>
                    );
                  })}
                </PathInputContainer>
              );
            }
            return (
              <PathInputContainer>
                {editedData.component_ids.map((cid, index) => {
                  const resolved = resolve_component_by_stored_id(
                    cid,
                    allTestTargets
                  );
                  const orphan = Boolean(cid.trim()) && !resolved;
                  const elsewhere_selected_ids = new Set<string>();
                  editedData.component_ids.forEach((s, i) => {
                    if (i === index) {
                      return;
                    }
                    const t = s.trim();
                    if (t.length > 0) {
                      elsewhere_selected_ids.add(t);
                    }
                  });
                  const trimmed_cid = cid.trim();
                  const duplicate =
                    Boolean(trimmed_cid) &&
                    Boolean(resolved) &&
                    elsewhere_selected_ids.has(trimmed_cid);
                  const selectable_components = availableComponents.filter(
                    (c) => !elsewhere_selected_ids.has(c.id)
                  );
                  const currentConditions = getConditionsForId(trimmed_cid);
                  return (
                    <div key={`row-${index}`}>
                      <PathInputRow>
                        <PathSelect
                          value={cid}
                          onChange={(e) =>
                            handleComponentIdChange(
                              cid,
                              e.target.value,
                              index
                            )
                          }
                        >
                          <option value="">（選択してください）</option>
                          {orphan ? (
                            <option value={cid}>
                              {cid}（参照できません）
                            </option>
                          ) : null}
                          {duplicate && resolved ? (
                            <option value={cid}>
                              {resolved.name}（他行と重複）
                            </option>
                          ) : null}
                          {selectable_components.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </PathSelect>
                        <IconButton
                          onClick={() => removeComponentIdRow(index)}
                          title="この行を削除"
                          style={{ width: "32px", height: "32px" }}
                        >
                          {Icons.Delete}
                        </IconButton>
                      </PathInputRow>
                      {orphan ? (
                        <ComponentErrorText>
                          存在しない共通コンポーネントです
                        </ComponentErrorText>
                      ) : null}
                      {duplicate ? (
                        <ComponentErrorText>
                          同じ共通コンポーネントが他の行でも選択されています
                        </ComponentErrorText>
                      ) : null}
                      {trimmed_cid && !duplicate ? (
                        <ConditionsBlock>
                          <ConditionsBlockLabel>表示条件</ConditionsBlockLabel>
                          {currentConditions.map((cond, ci) => (
                            <PathInputRow key={`cond-${index}-${ci}`}>
                              <PathInput
                                value={cond}
                                onChange={(e) => {
                                  const next = currentConditions.map(
                                    (c, i) => (i === ci ? e.target.value : c)
                                  );
                                  writeConditionsForId(trimmed_cid, next);
                                }}
                                placeholder="例: プレミアム会員でログイン中"
                              />
                              <IconButton
                                onClick={() =>
                                  writeConditionsForId(
                                    trimmed_cid,
                                    currentConditions.filter(
                                      (_, i) => i !== ci
                                    )
                                  )
                                }
                                title="条件を削除"
                                style={{ width: "32px", height: "32px" }}
                              >
                                {Icons.Delete}
                              </IconButton>
                            </PathInputRow>
                          ))}
                          <Button
                            variant="outline"
                            onClick={() =>
                              writeConditionsForId(trimmed_cid, [
                                ...currentConditions,
                                "",
                              ])
                            }
                            style={{ marginTop: "4px" }}
                          >
                            {Icons.Add} 条件を追加
                          </Button>
                        </ConditionsBlock>
                      ) : null}
                    </div>
                  );
                })}
                <Button
                  variant="outline"
                  onClick={() => {
                    onDataChange({
                      ...editedData,
                      component_ids: [...editedData.component_ids, ""],
                    });
                  }}
                  style={{ marginTop: "8px" }}
                >
                  {Icons.Add} 共通コンポーネントを追加
                </Button>
              </PathInputContainer>
            );
          })()
        ) : (
          <ViewInfoValue>
            {(() => {
              const ids = unique_trimmed_component_ids(
                editedData.component_ids
              );
              if (ids.length === 0) {
                if (availableComponents.length === 0) {
                  return "共通コンポーネントが存在しません";
                }
                return "共通コンポーネントが設定されていません";
              }
              return (
                <PathList>
                  {ids.map((cid, index) => {
                    const resolved = resolve_component_by_stored_id(
                      cid,
                      allTestTargets
                    );
                    const conditionsForId = getConditionsForId(cid).filter(
                      (c) => c.trim() !== ""
                    );
                    return (
                      <div key={`ro-${index}-${cid}`}>
                        <PathItem>
                          {resolved ? resolved.name : cid}
                        </PathItem>
                        {!resolved ? (
                          <ComponentErrorText>
                            存在しない共通コンポーネントです
                          </ComponentErrorText>
                        ) : null}
                        {conditionsForId.length > 0 ? (
                          <ConditionsReadOnlyList>
                            {conditionsForId.map((cond, ci) => (
                              <ConditionsReadOnlyItem
                                key={`ro-cond-${index}-${ci}`}
                              >
                                {cond}
                              </ConditionsReadOnlyItem>
                            ))}
                          </ConditionsReadOnlyList>
                        ) : null}
                      </div>
                    );
                  })}
                </PathList>
              );
            })()}
          </ViewInfoValue>
        )}
      </ViewInfoGroup>
      ) : null}

      <ViewInfoGroup>
        <ViewInfoLabel>説明</ViewInfoLabel>
        {isEditing ? (
          <ViewInfoTextarea
            value={editedData.description}
            onChange={(e) =>
              onDataChange({ ...editedData, description: e.target.value })
            }
            placeholder={`${target_kind_label}の説明を入力してください`}
          />
        ) : (
          <ViewInfoValue style={{ minHeight: "200px", paddingTop: "8px" }}>
            {editedData.description ||
              viewTarget.description ||
              "説明が設定されていません"}
          </ViewInfoValue>
        )}
      </ViewInfoGroup>

      {!is_component_target ? (
      <ViewInfoGroup>
        {isEditing ? (
          <ViewInfoCheckboxContainer>
            <ViewInfoCheckbox
              type="checkbox"
              id="entry-point-checkbox"
              checked={editedData.entry_point}
              onChange={(e) =>
                onDataChange({ ...editedData, entry_point: e.target.checked })
              }
            />
            <ViewInfoCheckboxLabel htmlFor="entry-point-checkbox">
              エントリーポイント
              <ViewInfoDescription>
                AIがテストケースの画面遷移を考える際に入り口となる画面にチェックしてください。
                通常はログイン画面やトップページなど、ユーザーが最初にアクセスする画面が対象です。
              </ViewInfoDescription>
            </ViewInfoCheckboxLabel>
          </ViewInfoCheckboxContainer>
        ) : (
          <>
            <ViewInfoLabel>エントリーポイント</ViewInfoLabel>
            <ViewInfoValue>
              {editedData.entry_point ? "✓ はい" : "いいえ"}
              <ViewInfoDescription style={{ marginTop: "8px", padding: "0" }}>
                AIがテストケースの画面遷移を考える際に入り口となる画面にチェックしてください。
                通常はログイン画面やトップページなど、ユーザーが最初にアクセスする画面が対象です。
              </ViewInfoDescription>
            </ViewInfoValue>
          </>
        )}
      </ViewInfoGroup>
      ) : null}

      <ViewInfoGroup>
        <ViewInfoLabel>ステータス</ViewInfoLabel>
        {isEditing ? (
          <select
            value={editedData.status || "approved"}
            onChange={(e) =>
              onDataChange({
                ...editedData,
                status: e.target.value as ReviewStatus,
              })
            }
            style={{
              width: "100%",
              padding: "12px",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              fontSize: "0.875rem",
              backgroundColor: "white",
            }}
          >
            {(Object.keys(STATUS_LABELS) as ReviewStatus[]).map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        ) : (
          <ViewInfoValue>
            {(() => {
              const status =
                viewTarget.params &&
                typeof viewTarget.params === "object" &&
                "status" in viewTarget.params
                  ? (viewTarget.params.status as ReviewStatus)
                  : "approved";
              return (
                <StatusBadge status={status}>
                  {STATUS_LABELS[status] || STATUS_LABELS.approved}
                </StatusBadge>
              );
            })()}
          </ViewInfoValue>
        )}
      </ViewInfoGroup>

      <ViewInfoGroup>
        <ViewInfoLabel>自動アップロード回数</ViewInfoLabel>
        {isEditing ? (
          <ViewInfoInput
            type="number"
            min={0}
            value={editedData.auto_upload_count ?? 0}
            onChange={(e) =>
              onDataChange({
                ...editedData,
                auto_upload_count: parseInt(e.target.value, 10) || 0,
              })
            }
            placeholder="自動アップロード回数"
            style={{ width: "120px" }}
          />
        ) : (
          <ViewInfoValue>
            {(() => {
              const autoUploadCount =
                viewTarget.params &&
                typeof viewTarget.params === "object" &&
                "auto_upload_count" in viewTarget.params
                  ? Number(viewTarget.params.auto_upload_count) || 0
                  : 0;
              return autoUploadCount;
            })()}
            <ViewInfoDescription style={{ marginTop: "8px", padding: "0" }}>
              自動アップロードが行われた回数です。設定値(デフォルト3)回に達すると自動的にapprovedステータスに変更されます。
            </ViewInfoDescription>
          </ViewInfoValue>
        )}
      </ViewInfoGroup>
    </ViewInfoContainer>
  );
};

// 機能(feature)一覧。View 直下の feature ターゲット＋main procedure を
// procedureClient.fetchUsecases で合成して表示し、作成・編集・詳細表示は UseCaseEditModal で行う。
const UseCaseDisplay: React.FC<{
  viewTarget: TestTarget | null;
  features: UsecaseView[];
  isLoading: boolean;
  testcaseMappings?: import("../services/api").TestcaseMappings | null;
  /** #884: 機能作成（UseCaseEditModal）を開く。未指定/不可時は作成ボタンを出さない。 */
  onCreateFeature?: () => void;
  /** #884: 機能編集（UseCaseEditModal）を開く。 */
  onEditFeature?: (feature: UsecaseView) => void;
  /** 機能の詳細（UseCaseEditModal の view モード）を開く。行クリックで呼ぶ。 */
  onViewFeature?: (feature: UsecaseView) => void;
}> = ({
  viewTarget,
  features,
  isLoading,
  testcaseMappings,
  onCreateFeature,
  onEditFeature,
  onViewFeature,
}) => {
  // 機能(externalId=旧usecaseId)から関連テストケース数を取得
  const getFeatureTestcaseCount = (usecaseId: string): number => {
    if (!testcaseMappings || testcaseMappings.usecaseToTestcases.size === 0) {
      return 0;
    }
    const relatedMappings =
      testcaseMappings.usecaseToTestcases.get(usecaseId) || [];
    const uniqueTestCaseIds = new Set(relatedMappings.map((m) => m.testcaseId));
    return uniqueTestCaseIds.size;
  };

  if (viewTarget?.type === "component") {
    return (
      <EmptyState>
        <EmptyTitle>コンポーネントには機能を追加できません</EmptyTitle>
        <EmptyText>呼び出し先のビューに作成してください</EmptyText>
      </EmptyState>
    );
  }

  if (isLoading) {
    return (
      <EmptyState>
        <CircularProgress size={24} />
        <EmptyText>機能一覧を読み込み中...</EmptyText>
      </EmptyState>
    );
  }

  if (!features || features.length === 0) {
    return (
      <EmptyState>
        <EmptyTitle>機能がまだ定義されていません</EmptyTitle>
        <EmptyText>
          「機能を作成」から追加するか、「AIでターゲットを生成」や仕様書インポートで作成できます
        </EmptyText>
        {onCreateFeature && (
          <Button
            variant="primary"
            onClick={onCreateFeature}
            style={{ marginTop: 16 }}
          >
            <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
            機能を作成
          </Button>
        )}
      </EmptyState>
    );
  }

  return (
    <UseCaseSection>
      {onCreateFeature && (
        <FeatureToolbar>
          <Button variant="primary" onClick={onCreateFeature}>
            <AddIcon style={{ fontSize: 18, marginRight: 4 }} />
            機能を作成
          </Button>
        </FeatureToolbar>
      )}
      <UseCaseList>
        {features.map((feature) => {
          const status = getStatus({
            status: (feature.status ?? undefined) as ReviewStatus | undefined,
          });
          return (
            <UseCaseItem
              key={feature.featureTargetId}
              onClick={onViewFeature ? () => onViewFeature(feature) : undefined}
              title={onViewFeature ? "クリックで詳細を表示" : undefined}
              style={onViewFeature ? { cursor: "pointer" } : undefined}
            >
              <UseCaseContent>
                <UseCaseTitleRow>
                  <UseCaseTitle title={feature.title || "タイトルなし"}>
                    {feature.title || "タイトルなし"}
                  </UseCaseTitle>
                  <StatusBadge status={status}>
                    {STATUS_LABELS[status]}
                  </StatusBadge>
                  {onEditFeature && (
                    <Tooltip title="機能を編集">
                      <IconOnlyButton
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditFeature(feature);
                        }}
                        aria-label="機能を編集"
                      >
                        <EditIcon style={{ fontSize: 18 }} />
                      </IconOnlyButton>
                    </Tooltip>
                  )}
                </UseCaseTitleRow>
                <UseCaseIdText>ID: {feature.usecaseId}</UseCaseIdText>
                <UseCaseDescription title={feature.description}>
                  {feature.description}
                </UseCaseDescription>
                <div
                  style={{ display: "flex", gap: "12px", alignItems: "center" }}
                >
                  <UseCaseSteps>
                    <StepIndicator>{feature.steps?.length || 0}</StepIndicator>
                    件のステップ
                  </UseCaseSteps>
                  <UseCaseSteps>
                    <StepIndicator style={{ backgroundColor: "#10b981" }}>
                      {getFeatureTestcaseCount(feature.usecaseId)}
                    </StepIndicator>
                    件のテストケース
                  </UseCaseSteps>
                </div>
              </UseCaseContent>
            </UseCaseItem>
          );
        })}
      </UseCaseList>
    </UseCaseSection>
  );
};

// #884: 機能行の編集ボタン等に使うアイコンボタン
const IconOnlyButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  color: #64748b;
  display: inline-flex;
  padding: 4px;
  border-radius: 6px;
  &:hover {
    background: #f1f5f9;
  }
`;


interface ItemSpecificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  viewTarget: TestTarget | null;
  items: (TestTarget & { level?: number })[];
  onEditItem: (item: TestTarget) => void;
  onViewItem?: (item: TestTarget) => void;
  /** 削除完了時の成功トーストを呼び出し元で表示する場合は suppressSuccess: true を渡す（一括削除時の重複防止） */
  onDeleteItem?: (
    itemId: string,
    options?: { suppressSuccess?: boolean }
  ) => Promise<void>;
  onDeleteView?: (viewId: string) => Promise<void>;
  onAddItem: () => void;
  onWidthChange?: (width: number) => void;
  apiClient?: SSTFApiClient;
  workspaceId?: string;
  projectId?: string;
  allTestTargets?: TestTarget[];
  testcaseMappings?: import("../services/api").TestcaseMappings | null;
  /** @deprecated #884 で旧 UC 編集入口は廃止。呼び出し元互換のため型は残すが本コンポーネントでは未使用。 */
  onEditUseCase?: (usecase: UseCase, index: number) => void;
  /** @deprecated #884 で旧 UC 表示入口は廃止。呼び出し元互換のため型は残すが本コンポーネントでは未使用。 */
  onViewUseCase?: (usecase: UseCase, index: number) => void;
  onRefresh?: () => void;
  onUpdateTestTarget?: (updatedTarget: TestTarget) => void;
  operationLogService?: OperationLogService;
  // userToken removed - using Auth0 authentication
  apiConfig?: ApiConfig;
  onOpenReferenceTarget?: (target: TestTarget) => void;
  /** 「参照先を開く」で切り替えた後、直前の対象へ戻れるか */
  canGoBackToPreviousTarget?: boolean;
  /** 直前のパネル対象へ戻る */
  onBackToPreviousTarget?: () => void;
}

const ItemSpecificationPanel: React.FC<ItemSpecificationPanelProps> = ({
  isOpen,
  onClose,
  viewTarget,
  items,
  onEditItem,
  onViewItem,
  onDeleteItem,
  onDeleteView,
  onAddItem,
  onWidthChange,
  apiClient,
  workspaceId,
  projectId,
  allTestTargets = [],
  testcaseMappings,
  onRefresh,
  onUpdateTestTarget,
  operationLogService,
  // userToken removed - using Auth0 authentication
  apiConfig,
  onOpenReferenceTarget,
  canGoBackToPreviousTarget,
  onBackToPreviousTarget,
}) => {
  const { showSuccess, showError } = useToast();
  const spec_panel_label = spec_panel_target_kind_label(viewTarget);
  const is_component_panel_target = viewTarget?.type === "component";
  const grouping_view_id_for_operation_log = useMemo(() => {
    if (!viewTarget) return undefined;
    if (viewTarget.type === "component") {
      return find_parent_view_id(allTestTargets, viewTarget.id) ?? viewTarget.id;
    }
    return viewTarget.id;
  }, [viewTarget, allTestTargets]);
  console.log("ItemSpecificationPanel rendered with:", {
    isOpen,
    viewTarget: viewTarget?.name,
    viewTargetId: viewTarget?.id,
    itemsCount: items?.length,
    items: items?.map((i) => ({
      id: i.id,
      name: i.name,
      type: i.type,
      parentId: i.parentId,
      level: i.level,
    })),
    activeTabInitial: "view",
  });

  const [activeTab, setActiveTab] = useState<"view" | "items" | "usecases">(
    "view"
  );
  const [isEditingView, setIsEditingView] = useState(false);
  const [isSavingView, setIsSavingView] = useState(false);
  // #884: View 直下の feature 一覧（read-only）。procedureClient.fetchUsecases で合成。
  const [featureList, setFeatureList] = useState<UsecaseView[]>([]);
  const [isLoadingFeatures, setIsLoadingFeatures] = useState(false);
  // #884: 機能(feature+procedure)の作成・編集は元の UseCaseEditModal を流用する
  const [usecaseModalOpen, setUsecaseModalOpen] = useState(false);
  const [usecaseModalMode, setUsecaseModalMode] = useState<
    "view" | "create" | "edit"
  >("create");
  const [usecaseModalUseCase, setUsecaseModalUseCase] = useState<UseCase | null>(
    null
  );
  const [usecaseModalExisting, setUsecaseModalExisting] =
    useState<SaveFeatureExisting | null>(null);
  const [isBulkDeleteModeItems, setIsBulkDeleteModeItems] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [expandedRefIds, setExpandedRefIds] = useState<Set<string>>(new Set());
  const toggleRefExpanded = (refId: string) => {
    setExpandedRefIds((prev) => {
      const next = new Set(prev);
      if (next.has(refId)) {
        next.delete(refId);
      } else {
        next.add(refId);
      }
      return next;
    });
  };
  const [editedViewData, setEditedViewData] = useState<{
    name: string;
    description: string;
    paths: ViewPath[];
    conditions: string[];
    component_ids: string[];
    entry_point: boolean;
    status?: ReviewStatus;
    auto_upload_count?: number;
    componentConditions: ComponentConditionEntry[];
  }>({
    name: "",
    description: "",
    paths: [],
    conditions: [],
    component_ids: [""],
    entry_point: false,
    status: "approved",
    auto_upload_count: 0,
    componentConditions: [],
  });

  // viewTargetが変更された時にeditedViewDataを更新
  useEffect(() => {
    if (viewTarget) {
      const viewPaths = getViewPaths(viewTarget.params);
      const entryPoint =
        viewTarget.params &&
        typeof viewTarget.params === "object" &&
        "entry_point" in viewTarget.params
          ? Boolean(viewTarget.params.entry_point)
          : false;
      const status =
        viewTarget.params &&
        typeof viewTarget.params === "object" &&
        "status" in viewTarget.params
          ? (viewTarget.params.status as ReviewStatus)
          : "approved";
      const autoUploadCount =
        viewTarget.params &&
        typeof viewTarget.params === "object" &&
        "auto_upload_count" in viewTarget.params
          ? Number(viewTarget.params.auto_upload_count) || 0
          : 0;
      const componentIds = get_component_ids_from_view_params(
        viewTarget.params
      );
      const componentConditionsValue = getViewComponentConditions(
        viewTarget.params
      );
      const viewConditionsValue = getParamsConditions(viewTarget.params);

      setEditedViewData({
        name: viewTarget.name,
        description: viewTarget.description || "",
        paths: viewPaths.length > 0 ? viewPaths : [{ path: "" }],
        conditions: viewConditionsValue,
        component_ids:
          componentIds.length > 0 ? componentIds : [""],
        entry_point: entryPoint,
        status: status,
        auto_upload_count: autoUploadCount,
        componentConditions: componentConditionsValue,
      });
    }
  }, [viewTarget]);

  useEffect(() => {
    if (activeTab !== "items") {
      setIsBulkDeleteModeItems(false);
      setSelectedItemIds(new Set());
    }
  }, [activeTab]);

  useEffect(() => {
    setIsBulkDeleteModeItems(false);
    setSelectedItemIds(new Set());
  }, [viewTarget?.id]);

  // #884: View 直下の feature ターゲット＋main procedure を合成して read-only 表示する。
  // 旧 params.usecases[] は読まない。取得失敗時は空一覧にフォールバック（安全側）。
  // 機能作成後の再取得にも使うため useCallback で切り出す。
  const reloadFeatures = useCallback(async () => {
    if (
      !apiClient ||
      !workspaceId ||
      !projectId ||
      !viewTarget ||
      viewTarget.type === "component"
    ) {
      setFeatureList([]);
      setIsLoadingFeatures(false);
      return;
    }
    setIsLoadingFeatures(true);
    try {
      const features = await fetchUsecases(
        apiClient,
        workspaceId,
        projectId,
        viewTarget.id
      );
      setFeatureList(features);
    } catch (error) {
      console.error("機能(feature)一覧の取得に失敗しました:", error);
      setFeatureList([]);
    } finally {
      setIsLoadingFeatures(false);
    }
  }, [apiClient, workspaceId, projectId, viewTarget]);

  useEffect(() => {
    reloadFeatures();
  }, [reloadFeatures]);

  // #884: 機能(feature) を UseCase 形へ変換して元の UseCaseEditModal で開く。
  const openCreateUsecase = useCallback(() => {
    const blankId =
      (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
        ?.randomUUID?.() ?? "";
    setUsecaseModalUseCase({
      usecase_id: blankId,
      name: "",
      title: "",
      description: "",
      steps: [],
      policies: [],
      parameters: [],
      status: "draft",
    });
    setUsecaseModalExisting(null);
    setUsecaseModalMode("create");
    setUsecaseModalOpen(true);
  }, []);

  const openUsecaseModal = useCallback(
    (feature: UsecaseView, mode: "view" | "edit") => {
      const policies: Policy[] = (feature.policies || []).map((pp) => ({
        policy_id: pp.policyId,
        name: pp.name,
        description: pp.description ?? "",
        changes: pp.changes,
        parameters: pp.parameters,
        status: pp.status as ReviewStatus | undefined,
      }));
      setUsecaseModalUseCase({
        usecase_id: feature.usecaseId,
        name: feature.name,
        title: feature.title,
        description: feature.description,
        steps: feature.steps,
        policies,
        parameters: feature.parameters,
        status: (feature.status ?? undefined) as ReviewStatus | undefined,
      });
      setUsecaseModalExisting({
        featureTargetId: feature.featureTargetId,
        procedureId: feature.procedureId,
        featureTypeId: feature.featureTypeId,
        featureValue: feature.featureValue,
        displayOrder: feature.displayOrder,
      });
      setUsecaseModalMode(mode);
      setUsecaseModalOpen(true);
    },
    []
  );

  const openEditUsecase = useCallback(
    (feature: UsecaseView) => openUsecaseModal(feature, "edit"),
    [openUsecaseModal]
  );

  const openViewUsecase = useCallback(
    (feature: UsecaseView) => openUsecaseModal(feature, "view"),
    [openUsecaseModal]
  );

  // #884: UseCaseEditModal の onSave を受けて feature+procedure として保存する（streamlit 非依存）。
  const handleSaveUsecase = useCallback(
    async (useCase: UseCase) => {
      if (!apiClient || !workspaceId || !projectId || !viewTarget) {
        showError("機能の保存に必要な情報が不足しています");
        return;
      }
      try {
        await saveFeatureFromUseCase(
          apiClient,
          workspaceId,
          projectId,
          viewTarget.id,
          useCase,
          usecaseModalExisting,
          featureList.length + 1
        );
        setUsecaseModalOpen(false);
        await reloadFeatures();
        onRefresh?.();
        showSuccess(
          usecaseModalExisting
            ? `機能「${useCase.title || useCase.name}」を更新しました`
            : `機能「${useCase.title || useCase.name}」を作成しました`
        );
      } catch (error) {
        console.error("機能の保存に失敗しました:", error);
        showError(
          `機能の保存に失敗しました: ${
            error instanceof Error ? error.message : "不明なエラー"
          }`
        );
      }
    },
    [
      apiClient,
      workspaceId,
      projectId,
      viewTarget,
      usecaseModalExisting,
      featureList.length,
      reloadFeatures,
      onRefresh,
      showSuccess,
      showError,
    ]
  );

  // View 直下の項目が 0 件になったら一括削除モードを解除（共通コンポーネント経由の行のみのとき）
  useEffect(() => {
    if (items.length === 0 && isBulkDeleteModeItems) {
      setIsBulkDeleteModeItems(false);
      setSelectedItemIds(new Set());
    }
  }, [items.length, isBulkDeleteModeItems]);

  // ビュー編集開始
  const handleStartEditView = () => {
    if (!viewTarget) return;

    console.log("📝 ビュー編集開始 - viewTarget:", viewTarget);
    console.log("📝 viewTarget.params:", viewTarget.params);

    const viewPaths = getViewPaths(viewTarget.params);
    const entryPoint =
      viewTarget.params &&
      typeof viewTarget.params === "object" &&
      "entry_point" in viewTarget.params
        ? Boolean(viewTarget.params.entry_point)
        : false;
    const status =
      viewTarget.params &&
      typeof viewTarget.params === "object" &&
      "status" in viewTarget.params
        ? (viewTarget.params.status as ReviewStatus)
        : "approved";
    const autoUploadCount =
      viewTarget.params &&
      typeof viewTarget.params === "object" &&
      "auto_upload_count" in viewTarget.params
        ? Number(viewTarget.params.auto_upload_count) || 0
        : 0;

    console.log("📝 取得したstatus:", status);
    console.log("📝 取得したauto_upload_count:", autoUploadCount);

    const componentIds = get_component_ids_from_view_params(
      viewTarget.params
    );
    const componentConditionsValue = getViewComponentConditions(
      viewTarget.params
    );
    const viewConditionsValue = getParamsConditions(viewTarget.params);

    setEditedViewData({
      name: viewTarget.name,
      description: viewTarget.description || "",
      paths: viewPaths.length > 0 ? viewPaths : [{ path: "" }],
      conditions: viewConditionsValue,
      component_ids:
        componentIds.length > 0 ? componentIds : [""],
      entry_point: entryPoint,
      status: status,
      auto_upload_count: autoUploadCount,
      componentConditions: componentConditionsValue,
    });
    setIsEditingView(true);
  };

  // ビュー編集キャンセル
  const handleCancelEditView = () => {
    if (!viewTarget) {
      setIsEditingView(false);
      return;
    }

    // viewTargetから元の値を取得してリセット
    const viewPaths = getViewPaths(viewTarget.params);
    const entryPoint =
      viewTarget.params &&
      typeof viewTarget.params === "object" &&
      "entry_point" in viewTarget.params
        ? Boolean(viewTarget.params.entry_point)
        : false;
    const status =
      viewTarget.params &&
      typeof viewTarget.params === "object" &&
      "status" in viewTarget.params
        ? (viewTarget.params.status as ReviewStatus)
        : "approved";
    const autoUploadCount =
      viewTarget.params &&
      typeof viewTarget.params === "object" &&
      "auto_upload_count" in viewTarget.params
        ? Number(viewTarget.params.auto_upload_count) || 0
        : 0;

    const componentIds = get_component_ids_from_view_params(
      viewTarget.params
    );
    const componentConditionsValue = getViewComponentConditions(
      viewTarget.params
    );
    const viewConditionsValue = getParamsConditions(viewTarget.params);

    setIsEditingView(false);
    setEditedViewData({
      name: viewTarget.name,
      description: viewTarget.description || "",
      paths: viewPaths.length > 0 ? viewPaths : [{ path: "" }],
      conditions: viewConditionsValue,
      component_ids:
        componentIds.length > 0 ? componentIds : [""],
      entry_point: entryPoint,
      status: status,
      auto_upload_count: autoUploadCount,
      componentConditions: componentConditionsValue,
    });
  };

  // ビュー編集保存
  const handleSaveView = async () => {
    if (!viewTarget || !apiClient || !workspaceId || !projectId) {
      console.error("必要なパラメータが不足しています");
      return;
    }

    setIsSavingView(true);
    try {
      // paramsを更新する（statusは別途APIで処理されるため、ここでは含めない）
      // paths配列を設定（空のpathは除外）
      let updatedParams: Record<string, unknown>;
      if (viewTarget.type === "component") {
        updatedParams = {
          ...(viewTarget.params && typeof viewTarget.params === "object"
            ? { ...(viewTarget.params as Record<string, unknown>) }
            : {}),
        };
        updatedParams.auto_upload_count = editedViewData.auto_upload_count ?? 0;
      } else {
        const validPaths = editedViewData.paths.filter(
          (p) => p.path && p.path.trim() !== ""
        );
        updatedParams = setViewPaths(
          viewTarget.params || {},
          validPaths
        ) as Record<string, unknown>;
        updatedParams.entry_point = editedViewData.entry_point;
        updatedParams.auto_upload_count = editedViewData.auto_upload_count ?? 0;

        const validComponentIds = unique_trimmed_component_ids(
          editedViewData.component_ids
        );
        if (validComponentIds.length > 0) {
          (updatedParams as { component_ids?: string[] }).component_ids =
            validComponentIds;
        } else {
          delete (updatedParams as Record<string, unknown>).component_ids;
        }

        (updatedParams as {
          componentConditions?: ComponentConditionEntry[] | null;
        }).componentConditions = normalizeComponentConditionsForSave(
          editedViewData.componentConditions,
          validComponentIds
        );
        const validConditions = editedViewData.conditions
          .map((c) => c.trim())
          .filter((c) => c.length > 0);
        (updatedParams as { conditions?: string[] | null }).conditions =
          validConditions.length > 0 ? validConditions : null;
      }

      await apiClient.updateTestTarget(workspaceId, projectId, viewTarget.id, {
        name: editedViewData.name,
        description: editedViewData.description,
        status: editedViewData.status,
        params: updatedParams,
      });

      setIsEditingView(false);

      // ローカル状態更新またはリフレッシュ
      if (onUpdateTestTarget && viewTarget) {
        console.log("🔄 ItemSpecificationPanel: ローカル状態を更新します");

        // paramsにstatusとauto_upload_countを含める（APIに保存されている形式）
        const paramsWithStatus = {
          ...updatedParams,
          status: editedViewData.status,
          auto_upload_count: editedViewData.auto_upload_count,
        };

        const updatedTarget: TestTarget = {
          ...viewTarget,
          name: editedViewData.name,
          description: editedViewData.description,
          params: paramsWithStatus, // params内にstatusを含める
        };
        onUpdateTestTarget(updatedTarget);

        // 編集データも最新の保存内容で更新（保存された値を使用）
        const savedPaths = getViewPaths(updatedTarget.params);
        const savedCids = get_component_ids_from_view_params(
          updatedTarget.params
        );
        const savedComponentConditions = getViewComponentConditions(
          updatedTarget.params
        );
        const savedConditions = getParamsConditions(updatedTarget.params);
        setEditedViewData({
          name: updatedTarget.name,
          description: updatedTarget.description || "",
          paths: savedPaths.length > 0 ? savedPaths : [{ path: "" }],
          conditions: savedConditions,
          component_ids:
            savedCids.length > 0 ? savedCids : [""],
          entry_point: editedViewData.entry_point,
          status: editedViewData.status,
          auto_upload_count: editedViewData.auto_upload_count,
          componentConditions: savedComponentConditions,
        });
      } else if (onRefresh) {
        console.log("⚠️ ItemSpecificationPanel: リフレッシュを実行");
        onRefresh();
      }
      showSuccess(`${spec_panel_label}情報を保存しました`);
      console.log(`${spec_panel_label}情報が正常に更新されました`);
    } catch (error) {
      console.error(`${spec_panel_label}更新エラー:`, error);
      showError(
        `${spec_panel_label}の更新に失敗しました: ${
          error instanceof Error ? error.message : "不明なエラー"
        }`
      );
    } finally {
      setIsSavingView(false);
    }
  };

  // 項目一覧タブの一括削除モード開始
  const handleStartBulkDeleteModeItems = () => {
    setIsBulkDeleteModeItems(true);
    setSelectedItemIds(new Set());
  };

  // 項目一覧タブの一括削除モードキャンセル
  const handleCancelBulkDeleteModeItems = () => {
    setIsBulkDeleteModeItems(false);
    setSelectedItemIds(new Set());
  };

  // 項目一覧タブの選択トグル
  const handleToggleItemSelection = (itemId: string) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // 項目一覧タブの一括削除処理（入れ子構造を考慮）
  const handleBulkDeleteItems = () => {
    if (selectedItemIds.size === 0 || !onDeleteItem) {
      return;
    }

    // 削除対象のアイテムを取得
    const itemsToDelete = items.filter((item) => selectedItemIds.has(item.id));

    // 親IDのセットを作成（削除対象の親IDを確認）
    const parentIdsToDelete = new Set(
      itemsToDelete.map((item) => item.id).filter(Boolean)
    );

    // 親が削除対象に含まれている場合は、その配下のアイテムをスキップ
    const itemsToDeleteFiltered = itemsToDelete.filter((item) => {
      // 親IDがない場合は削除対象
      if (!item.parentId) {
        return true;
      }
      // 親IDが削除対象に含まれている場合はスキップ（親と一緒に削除されるため）
      if (parentIdsToDelete.has(item.parentId)) {
        return false;
      }
      return true;
    });

    const deleteCount = itemsToDeleteFiltered.length;
    const skippedCount = itemsToDelete.length - deleteCount;

    // 子アイテムの数を計算（親が削除される場合、その配下のアイテムも一緒に削除される）
    let totalChildCount = 0;
    itemsToDeleteFiltered.forEach((item) => {
      const childCount = items.filter((i) => i.parentId === item.id).length;
      totalChildCount += childCount;
    });

    let confirmMessage = "";
    if (skippedCount > 0 && totalChildCount > 0) {
      confirmMessage = `選択された${deleteCount}件のアイテムとその配下のすべてのアイテム（${totalChildCount}個）を削除しますか？\n\n${skippedCount}件の子アイテムは親と一緒に削除されるためスキップされます。\n\nこの操作は取り消せません。`;
    } else if (skippedCount > 0) {
      confirmMessage = `${deleteCount}件のアイテムを削除します。\n\n${skippedCount}件の子アイテムは親と一緒に削除されるためスキップされます。\n\nこの操作は取り消せません。`;
    } else if (totalChildCount > 0) {
      confirmMessage = `選択された${deleteCount}件のアイテムとその配下のすべてのアイテム（${totalChildCount}個）を削除しますか？\n\nこの操作は取り消せません。`;
    } else {
      confirmMessage = `選択された${deleteCount}件のアイテムを削除しますか？\n\nこの操作は取り消せません。`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // 親を先に削除するため、親IDがないものから順に削除
    const sortedItems = itemsToDeleteFiltered.sort((a, b) => {
      // 親IDがないもの（親アイテム）を先に
      if (!a.parentId && b.parentId) return -1;
      if (a.parentId && !b.parentId) return 1;
      return 0;
    });

    // すべての削除を同期的に呼び出す（画面のリロードを一度だけにする）
    // NOTE: 一括削除時は成功トーストをここで1回だけ表示するため、各呼び出しで suppressSuccess を渡す
    sortedItems.forEach((item) => {
      onDeleteItem(item.id, { suppressSuccess: true });
    });

    // 選択状態をクリア
    setSelectedItemIds(new Set());
    setIsBulkDeleteModeItems(false);

    showSuccess(`${deleteCount}件のアイテムを削除しました`);
  };

  // 遷移先のIDから名前を取得する関数
  const getTransitionTargetName = (targetId: string): string => {
    const target = allTestTargets.find((t: TestTarget) => t.id === targetId);
    return target ? target.name : targetId; // 見つからない場合はIDをそのまま表示
  };

  // 子アイテムを持つかどうかを判定（View 直下のみに限らず全ターゲットから検索）
  const hasChildren = (item: TestTarget): boolean => {
    return allTestTargets.some(
      (i) =>
        i.parentId === item.id &&
        (i.type === "item" || i.type === "section" || i.type === "overlay")
    );
  };

  // 削除ハンドラー
  const handleDeleteItem = async (item: TestTarget) => {
    const childCount = allTestTargets.filter(
      (i) =>
        i.parentId === item.id &&
        (i.type === "item" || i.type === "section" || i.type === "overlay")
    ).length;
    const message =
      childCount > 0
        ? `「${item.name}」とその子項目（${childCount}個）を削除しますか？\n\nこの操作は取り消せません。`
        : `「${item.name}」を削除しますか？\n\nこの操作は取り消せません。`;

    if (window.confirm(message)) {
      try {
        if (onDeleteItem) {
          await onDeleteItem(item.id);
        }
      } catch (error) {
        console.error("削除エラー:", error);
        showError(
          `削除に失敗しました: ${
            error instanceof Error ? error.message : "不明なエラー"
          }`
        );
      }
    }
  };

  // View削除ハンドラー
  const handleDeleteView = async () => {
    console.log("🗑️ View削除ボタンがクリックされました", {
      viewTarget,
      onDeleteView,
    });

    if (!viewTarget) {
      console.warn("⚠️ viewTargetがnullです");
      return;
    }

    const itemCount = items.length;
    const message =
      itemCount > 0
        ? `「${viewTarget.name}」とその配下のすべての項目（${itemCount}個）を削除しますか？\n\nこの操作は取り消せません。`
        : `「${viewTarget.name}」を削除しますか？\n\nこの操作は取り消せません。`;

    console.log("🗑️ 削除確認ダイアログを表示", { message });

    if (window.confirm(message)) {
      console.log("🗑️ ユーザーが削除を確認しました");
      try {
        if (onDeleteView) {
          console.log("🗑️ onDeleteViewを実行中...", viewTarget.id);
          await onDeleteView(viewTarget.id);
          console.log("🗑️ onDeleteView完了、パネルを閉じます");
          onClose(); // 削除後にパネルを閉じる
        } else {
          console.warn("⚠️ onDeleteViewが未定義です");
        }
      } catch (error) {
        console.error("❌ View削除エラー:", error);
        showError(
          `削除に失敗しました: ${
            error instanceof Error ? error.message : "不明なエラー"
          }`
        );
      }
    } else {
      console.log("🗑️ ユーザーが削除をキャンセルしました");
    }
  };

  // AIでターゲットを生成ダイアログを開く
  const handleOperationLogImport = () => {
    if (!viewTarget) {
      showError(`${spec_panel_label}が選択されていません`);
      return;
    }

    if (!apiClient) {
      showError("API設定が有効ではありません。API設定を確認してください。");
      return;
    }

    if (!workspaceId || workspaceId.trim() === "") {
      showError(
        "ワークスペースIDが設定されていません。API設定を確認してください。"
      );
      return;
    }

    if (!projectId || projectId.trim() === "") {
      showError(
        "プロジェクトIDが設定されていません。API設定を確認してください。"
      );
      return;
    }

    // View のパスを取得（複数パスのいずれかが存在するかチェック）
    const viewPaths = getViewPaths(viewTarget.params);

    if (viewTarget.type !== "component" && viewPaths.length === 0) {
      showError(
        `${spec_panel_label}にパスが設定されていません。${spec_panel_label}設定でパスを追加してください。`
      );
      return;
    }

    setShowImportDialog(true);
  };

  // インポートダイアログを閉じる
  const handleImportDialogClose = () => {
    setShowImportDialog(false);
  };

  // テストターゲット生成完了（確認ダイアログを表示）
  const handleResultsGenerated = (result: GenerateFromOperationLogResponse) => {
    console.log("🎯 handleResultsGenerated - 受信したレスポンス:", result);
    setGeneratedTargets(result.page_results);
    setGeneratedTraceId(result.trace_id); // AIジョブモニタリング用トレースID
    setGeneratedTraceparent(result.traceparent); // W3C Trace Context
    setGeneratedGenerationNote(result.generation_note);
    setGeneratedGenerationNoteLinkUrl(result.generation_note_link_url);
    setShowConfirmDialog(true);
  };

  // 登録完了後の処理
  const handleRegistrationComplete = () => {
    setShowConfirmDialog(false);
    setGeneratedTargets([]);
    setGeneratedTraceId(undefined); // トレースIDをリセット
    setGeneratedTraceparent(undefined); // traceparentをリセット
    setGeneratedGenerationNote(undefined);
    setGeneratedGenerationNoteLinkUrl(undefined);
    showSuccess("テストターゲットを登録しました");

    // リフレッシュしてデータを再読み込み
    if (onRefresh) {
      onRefresh();
    }
  };

  const [panelWidth, setPanelWidth] = useState(600);
  const [isResizing, setIsResizing] = useState(false);
  const [automationStats, setAutomationStats] = useState<
    Record<string, TestTargetAutomationStats>
  >({});
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [isProcessingOperationLog, setIsProcessingOperationLog] =
    useState(false);
  const [processingMessage, setProcessingMessage] = useState("");
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [generatedTargets, setGeneratedTargets] = useState<PageResult[]>([]);
  const [generatedTraceId, setGeneratedTraceId] = useState<string | undefined>(undefined);
  const [generatedTraceparent, setGeneratedTraceparent] = useState<string | undefined>(undefined);
  const [generatedGenerationNote, setGeneratedGenerationNote] = useState<string | undefined>(undefined);
  const [generatedGenerationNoteLinkUrl, setGeneratedGenerationNoteLinkUrl] = useState<string | undefined>(undefined);
  const startX = useRef(0);
  const startWidth = useRef(0);

  // 列幅の状態管理
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 30, // 項目名
    labels: 11, // ラベル
    type: 8, // タイプ
    mask: 8, // マスク種別
    required: 6, // 必須
    testcases: 14, // テストケース数
    transitions: 14, // 画面遷移
    actions: 9, // 操作
  });
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const columnResizeStartX = useRef(0);
  const columnResizeStartWidth = useRef(0);

  // パネル幅の制限
  const MIN_WIDTH = 300;
  const MAX_WIDTH = 1200;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      startX.current = e.clientX;
      startWidth.current = panelWidth;
    },
    [panelWidth]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;

      const deltaX = startX.current - e.clientX;
      const newWidth = Math.min(
        MAX_WIDTH,
        Math.max(MIN_WIDTH, startWidth.current + deltaX)
      );

      setPanelWidth(newWidth);
      onWidthChange?.(newWidth);
    },
    [isResizing, onWidthChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // 列幅リサイズのイベントハンドラー
  const handleColumnResizeStart = useCallback(
    (e: React.MouseEvent, columnKey: string) => {
      e.preventDefault();
      e.stopPropagation();
      setResizingColumn(columnKey);
      columnResizeStartX.current = e.clientX;
      columnResizeStartWidth.current = columnWidths[columnKey];
    },
    [columnWidths]
  );

  const handleColumnResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!resizingColumn) return;

      const deltaX = e.clientX - columnResizeStartX.current;
      const tableWrapper = document.querySelector('[data-table-wrapper]') as HTMLElement;
      if (!tableWrapper) return;

      const tableWidth = tableWrapper.offsetWidth;
      const deltaPercent = (deltaX / tableWidth) * 100;
      const newWidth = Math.max(5, Math.min(50, columnResizeStartWidth.current + deltaPercent));

      setColumnWidths((prev) => ({
        ...prev,
        [resizingColumn]: newWidth,
      }));
    },
    [resizingColumn]
  );

  const handleColumnResizeEnd = useCallback(() => {
    setResizingColumn(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // 列幅リサイズのイベントリスナー
  useEffect(() => {
    if (resizingColumn) {
      document.addEventListener("mousemove", handleColumnResizeMove);
      document.addEventListener("mouseup", handleColumnResizeEnd);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleColumnResizeMove);
        document.removeEventListener("mouseup", handleColumnResizeEnd);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };
    }
  }, [resizingColumn, handleColumnResizeMove, handleColumnResizeEnd]);

  // パネル幅変更時に親コンポーネントに通知
  useEffect(() => {
    if (isOpen) {
      onWidthChange?.(panelWidth);
    }
  }, [isOpen, panelWidth, onWidthChange]);

  // ESCキーでパネルを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        // 他の編集モーダルが開いている場合はItemSpecificationPanelを閉じない
        const hasActiveEditModal = document.querySelector(
          '[data-testid="test-target-detail-edit"], [data-testid="usecase-edit-modal"]'
        );

        if (!hasActiveEditModal) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [isOpen, onClose]);

  /** component_ids 由来のフラット項目 + 表用のコンポーネント行／項目行 */
  const { componentItemsFromIds, componentTableDisplayRows } = useMemo((): {
    componentItemsFromIds: PanelItemRow[];
    componentTableDisplayRows: ComponentTableDisplayRow[];
  } => {
    if (!viewTarget || !allTestTargets?.length) {
      return { componentItemsFromIds: [], componentTableDisplayRows: [] };
    }
    const displayRows = build_component_table_display_rows(
      viewTarget,
      allTestTargets
    );
    const flatRows: PanelItemRow[] = [];
    for (const r of displayRows) {
      if (r.rowKind === "item") {
        flatRows.push(r.panelRow);
      }
    }
    return {
      componentItemsFromIds: flatRows,
      componentTableDisplayRows: displayRows,
    };
  }, [viewTarget, allTestTargets]);

  /** 統計用: component 経由と View 直下を ID で重複除去 */
  const allItemTabTargetsForStats = useMemo(() => {
    const seen = new Set<string>();
    const out: TestTarget[] = [];
    for (const r of componentItemsFromIds) {
      if (!seen.has(r.id)) {
        seen.add(r.id);
        out.push(r);
      }
    }
    for (const r of items) {
      if (!seen.has(r.id)) {
        seen.add(r.id);
        out.push(r);
      }
    }
    return out;
  }, [componentItemsFromIds, items]);

  const itemTabTotalCount =
    componentItemsFromIds.length + items.length;

  // テストケースマッピングからアイテムの統計情報を生成
  useEffect(() => {
    console.log("ItemSpecificationPanel - テストケースマッピング処理開始:", {
      hasTestcaseMappings: !!testcaseMappings,
      testTargetMappingsSize: testcaseMappings?.testTargetToTestcases.size || 0,
      itemsCount: allItemTabTargetsForStats.length,
      itemIds: allItemTabTargetsForStats.map((i) => i.id),
    });

    if (
      !testcaseMappings ||
      testcaseMappings.testTargetToTestcases.size === 0
    ) {
      console.log("ItemSpecificationPanel - テストケースマッピングが空です");
      setIsLoadingStats(false);
      setAutomationStats({});
      setStatsError(null);
      return;
    }

    const stats: Record<string, TestTargetAutomationStats> = {};

    allItemTabTargetsForStats.forEach((item) => {
      const relatedMappings =
        testcaseMappings.testTargetToTestcases.get(item.id) || [];
      console.log(
        `ItemSpecificationPanel - アイテム ${item.name} (${item.id}) の関連テストケース数:`,
        relatedMappings.length
      );
      if (relatedMappings.length > 0) {
        // ユニークなテストケース数を計算
        const uniqueTestCaseIds = new Set(
          relatedMappings.map((m) => m.testcaseId)
        );
        const testcaseNames = Array.from(uniqueTestCaseIds).map((tcId) => {
          const mapping = relatedMappings.find((m) => m.testcaseId === tcId);
          return mapping?.testcaseName || tcId;
        });

        stats[item.id] = {
          test_target_id: item.id,
          total_testcases: uniqueTestCaseIds.size,
          testcase_names: testcaseNames,
        };
        console.log(
          `✅ ItemSpecificationPanel - 統計生成: ${item.name}`,
          stats[item.id]
        );
      } else {
        console.log(
          `❌ ItemSpecificationPanel - アイテム ${item.name} (${item.id}) の関連テストケースが見つかりません`
        );
      }
    });

    console.log("ItemSpecificationPanel - 最終生成された統計:", stats);
    console.log(
      "ItemSpecificationPanel - 統計のキー数:",
      Object.keys(stats).length
    );
    setIsLoadingStats(false);
    setAutomationStats(stats);
    setStatsError(null);
  }, [testcaseMappings, allItemTabTargetsForStats]);

  const renderSimpleTableView = () => {
    const renderTableHeaderRow = (variant: "component" | "view") => {
      const headBg =
        variant === "component"
          ? { background: "#ccfbf1" }
          : { background: "#f1f5f9" };
      return (
        <TableRow style={headBg}>
          {variant === "view" && isBulkDeleteModeItems && (
            <TableHeaderCell width="5%">
              <div style={{ display: "flex", justifyContent: "center" }}>
                <UseCaseCheckbox
                  type="checkbox"
                  checked={Boolean(
                    items.length > 0 &&
                      items.every((item) => {
                        if (
                          item.parentId &&
                          selectedItemIds.has(item.parentId)
                        ) {
                          return true;
                        }
                        return selectedItemIds.has(item.id);
                      })
                  )}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const allItemIds = new Set<string>();
                      items.forEach((item) => {
                        if (
                          item.parentId &&
                          allItemIds.has(item.parentId)
                        ) {
                          return;
                        }
                        allItemIds.add(item.id);
                      });
                      setSelectedItemIds(allItemIds);
                    } else {
                      setSelectedItemIds(new Set());
                    }
                  }}
                  aria-label="すべて選択"
                />
              </div>
            </TableHeaderCell>
          )}
          <TableHeaderCell width={`${columnWidths.name}%`}>
            項目名
            <ColumnResizeHandle
              isResizing={resizingColumn === "name"}
              onMouseDown={(e) => handleColumnResizeStart(e, "name")}
            />
          </TableHeaderCell>
          <TableHeaderCell width={`${columnWidths.labels}%`}>
            ラベル
            <ColumnResizeHandle
              isResizing={resizingColumn === "labels"}
              onMouseDown={(e) => handleColumnResizeStart(e, "labels")}
            />
          </TableHeaderCell>
          <TableHeaderCell width={`${columnWidths.type}%`}>
            タイプ
            <ColumnResizeHandle
              isResizing={resizingColumn === "type"}
              onMouseDown={(e) => handleColumnResizeStart(e, "type")}
            />
          </TableHeaderCell>
          <TableHeaderCell width={`${columnWidths.mask}%`}>
            マスク種別
            <ColumnResizeHandle
              isResizing={resizingColumn === "mask"}
              onMouseDown={(e) => handleColumnResizeStart(e, "mask")}
            />
          </TableHeaderCell>
          <TableHeaderCell width={`${columnWidths.required}%`}>
            必須
            <ColumnResizeHandle
              isResizing={resizingColumn === "required"}
              onMouseDown={(e) => handleColumnResizeStart(e, "required")}
            />
          </TableHeaderCell>
          <TableHeaderCell width={`${columnWidths.testcases}%`}>
            テストケース数
            <ColumnResizeHandle
              isResizing={resizingColumn === "testcases"}
              onMouseDown={(e) => handleColumnResizeStart(e, "testcases")}
            />
          </TableHeaderCell>
          <TableHeaderCell width={`${columnWidths.transitions}%`}>
            画面遷移
            <ColumnResizeHandle
              isResizing={resizingColumn === "transitions"}
              onMouseDown={(e) => handleColumnResizeStart(e, "transitions")}
            />
          </TableHeaderCell>
          <TableHeaderCell width={`${columnWidths.actions}%`}>
            操作
            <ColumnResizeHandle
              isResizing={resizingColumn === "actions"}
              onMouseDown={(e) => handleColumnResizeStart(e, "actions")}
            />
          </TableHeaderCell>
        </TableRow>
      );
    };

    const renderComponentHeaderRow = (row: {
      rowKind: "component_header";
      rowKey: string;
      name: string;
      depth: number;
    }) => {
      const pad = row.depth * 16;
      return (
        <TableRow
          key={row.rowKey}
          style={{ backgroundColor: "#ecfdf5" }}
        >
          <TableCell width={`${columnWidths.name}%`}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: pad,
                gap: 6,
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              {row.depth > 0 ? (
                <span
                  style={{
                    color: "#94a3b8",
                    fontSize: "0.85rem",
                    flexShrink: 0,
                    userSelect: "none",
                  }}
                  aria-hidden
                >
                  ┗
                </span>
              ) : null}
              <ViewModuleIcon
                sx={{ fontSize: 18, color: "#0d9488", flexShrink: 0 }}
              />
              <span style={{ color: "#334155" }}>{row.name}</span>
            </div>
          </TableCell>
          <TableCell width={`${columnWidths.labels}%`}>
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width={`${columnWidths.type}%`}>
            <div
              style={{
                fontSize: "0.6rem",
                color: "#94a3b8",
                textAlign: "center",
              }}
            >
              コンポーネント
            </div>
          </TableCell>
          <TableCell width={`${columnWidths.mask}%`}>
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width={`${columnWidths.required}%`}>
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width={`${columnWidths.testcases}%`}>
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width={`${columnWidths.transitions}%`}>
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
          <TableCell width={`${columnWidths.actions}%`}>
            <span style={{ fontSize: "0.6rem", color: "#94a3b8" }}>—</span>
          </TableCell>
        </TableRow>
      );
    };

    const renderItemRow = (
      item: PanelItemRow,
      variant: "component" | "view",
      rowKey?: string,
      readOnly: boolean = false
    ) => {
      const isViewDirectRow = variant === "view";
      const typeValue = item.type as any;
      const typeName =
        typeof typeValue === "object" && typeValue?.name
          ? typeValue.name
          : typeValue;
      const itemType = typeName?.toLowerCase();

      if (
        itemType !== "item" &&
        itemType !== "section" &&
        itemType !== "overlay"
      ) {
        return null;
      }

      // NOTE: selector 無し Item（CSV インポート等）でも transitions 等を表示する
      const details = as_item_details(item.params) ?? ({} as ItemDetails);

      const itemStatus = getStatus(item);
      const hasPendingStatus = isPendingReview(itemStatus);

      const isParentSelected = Boolean(
        item.parentId && selectedItemIds.has(item.parentId)
      );
      const isItemSelected = Boolean(
        selectedItemIds.has(item.id) || isParentSelected
      );

      const chain_depth = item.componentChainDepth ?? 0;
      const item_level = item.level || 0;
      const component_visual_level =
        variant === "component" ? chain_depth + item_level : item_level;

      return (
        <TableRow
          key={rowKey ?? `${variant}-${item.id}`}
          style={{
            backgroundColor: hasPendingStatus
              ? itemStatus === "draft"
                ? "#fff4e6"
                : itemStatus === "need_review"
                  ? "#ffe0b2"
                  : "#ffcc80"
              : "transparent",
          }}
        >
          {isViewDirectRow && isBulkDeleteModeItems && (
            <TableCell width="5%">
              <div style={{ display: "flex", justifyContent: "center" }}>
                <UseCaseCheckbox
                  type="checkbox"
                  checked={isItemSelected}
                  disabled={isParentSelected}
                  onChange={() => {
                    if (!isParentSelected) {
                      handleToggleItemSelection(item.id);
                    }
                  }}
                  aria-label={`${item.name}を削除対象に選択`}
                />
              </div>
            </TableCell>
          )}
          <TableCell width={`${columnWidths.name}%`}>
            {isViewDirectRow ? (
              <HierarchyContainer level={item.level || 0}>
                <ItemIcon isParent={hasChildren(item)}>
                  {hasChildren(item) ? (
                    <StyledFolderIcon />
                  ) : itemType === "section" ? (
                    <StyledDashboardIcon />
                  ) : itemType === "overlay" ? (
                    <StyledLayersIcon />
                  ) : (
                    <StyledArticleIcon />
                  )}
                </ItemIcon>
                <ItemNameContainer>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "2px",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        color: "#667eea",
                        textDecoration: "none",
                        flex: 1,
                      }}
                      title={`${item.name} - クリックで詳細を表示`}
                      onClick={() => onViewItem?.(item)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {item.name}
                    </div>
                  </div>
                  {details?.text && (
                    <div
                      style={{
                        fontSize: "0.6rem",
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={details.text}
                    >
                      {details.text.substring(0, 15)}
                      {details.text.length > 15 && "..."}
                    </div>
                  )}
                </ItemNameContainer>
              </HierarchyContainer>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                  paddingLeft: component_visual_level * 16,
                  boxSizing: "border-box",
                }}
              >
                {component_visual_level > 0 ? (
                  <span
                    style={{
                      color: "#94a3b8",
                      fontSize: "0.85rem",
                      lineHeight: 1.5,
                      marginRight: 4,
                      flexShrink: 0,
                      userSelect: "none",
                    }}
                    aria-hidden
                  >
                    ┗
                  </span>
                ) : null}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <ItemIcon isParent={hasChildren(item)}>
                    {hasChildren(item) ? (
                      <StyledFolderIcon />
                    ) : itemType === "section" ? (
                      <StyledDashboardIcon />
                    ) : itemType === "overlay" ? (
                      <StyledLayersIcon />
                    ) : (
                      <StyledArticleIcon />
                    )}
                  </ItemIcon>
                  <ItemNameContainer>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "2px",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          cursor: "pointer",
                          color: "#667eea",
                          textDecoration: "none",
                          flex: 1,
                        }}
                        title={`${item.name} - クリックで詳細を表示`}
                        onClick={() => onViewItem?.(item)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none";
                        }}
                      >
                        {item.name}
                      </div>
                    </div>
                    {details?.text && (
                      <div
                        style={{
                          fontSize: "0.6rem",
                          color: "#64748b",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={details.text}
                      >
                        {details.text.substring(0, 15)}
                        {details.text.length > 15 && "..."}
                      </div>
                    )}
                  </ItemNameContainer>
                </div>
              </div>
            )}
          </TableCell>
          <TableCell width={`${columnWidths.labels}%`}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                alignItems: "flex-start",
              }}
            >
              {isPendingReview(getStatus(item)) && (
                <StatusBadge
                  status={getStatus(item)}
                  style={{
                    fontSize: "0.65rem",
                    padding: "2px 6px",
                  }}
                >
                  {STATUS_LABELS[getStatus(item)]}
                </StatusBadge>
              )}
              {!hasSelector(item) && (
                <NoSelectorBadge>セレクタなし</NoSelectorBadge>
              )}
              {isManualOperation(item) && (
                <ManualOperationBadge>手動実行モード</ManualOperationBadge>
              )}
            </div>
          </TableCell>
          <TableCell width={`${columnWidths.type}%`}>
            <div
              style={{
                fontSize: "0.6rem",
                color: "#94a3b8",
                textAlign: "center",
              }}
            >
              {itemType === "section"
                ? "セクション"
                : itemType === "overlay"
                  ? "オーバーレイ"
                  : itemType === "item"
                    ? "アイテム"
                    : typeName || "不明"}
            </div>
          </TableCell>
          <TableCell width={`${columnWidths.mask}%`}>
            <div
              style={{
                fontSize: "0.6rem",
                color: "#64748b",
                textAlign: "center",
              }}
            >
              {formatMaskTypeForList(item.params)}
            </div>
          </TableCell>
          <TableCell width={`${columnWidths.required}%`}>
            <div style={{ display: "flex", justifyContent: "center" }}>
              {details?.htmlAttributes?.required ? (
                <RequiredIndicator title="必須" />
              ) : (
                <OptionalIndicator title="任意" />
              )}
            </div>
          </TableCell>
          <TableCell width={`${columnWidths.testcases}%`}>
            {automationStats[item.id] ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    justifyContent: "center",
                  }}
                >
                  <div style={{ fontSize: "0.6rem", fontWeight: 600 }}>
                    {automationStats[item.id].total_testcases}
                  </div>
                  <div style={{ fontSize: "0.55rem", color: "#64748b" }}>
                    件
                  </div>
                </div>
                <div style={{ fontSize: "0.5rem", color: "#64748b" }}>
                  テストケース
                </div>
              </div>
            ) : (
              <div
                style={{
                  fontSize: "0.6rem",
                  color: "#94a3b8",
                  textAlign: "center",
                }}
              >
                未定義
              </div>
            )}
          </TableCell>
          <TableCell width={`${columnWidths.transitions}%`}>
            {details?.transitions && details.transitions.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  maxWidth: "100%",
                  overflow: "hidden",
                }}
              >
                {details.transitions.map((transition, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      fontSize: "0.6rem",
                      color: "#1e293b",
                    }}
                  >
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                      }}
                      title={`→ ${getTransitionTargetName(
                        transition.targetId || ""
                      )}`}
                    >
                      →{" "}
                      {getTransitionTargetName(transition.targetId || "")}
                    </div>
                    {transition.status &&
                      isPendingReview(transition.status) && (
                        <StatusBadge
                          status={transition.status}
                          style={{
                            fontSize: "0.55rem",
                            padding: "1px 4px",
                            flexShrink: 0,
                          }}
                        >
                          {STATUS_LABELS[transition.status]}
                        </StatusBadge>
                      )}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: "0.6rem", color: "#94a3b8" }}>
                遷移なし
              </div>
            )}
          </TableCell>
          {readOnly ? (
            <TableCell width={`${columnWidths.actions}%`} />
          ) : (
            <TableCell width={`${columnWidths.actions}%`}>
              {isBulkDeleteModeItems ? (
                <div
                  style={{
                    display: "flex",
                    gap: "1px",
                    justifyContent: "flex-start",
                  }}
                >
                  <IconButton
                    onClick={() => onEditItem(item)}
                    style={{
                      width: "20px",
                      height: "20px",
                      fontSize: "0.7rem",
                    }}
                  >
                    {Icons.Edit}
                  </IconButton>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: "1px",
                    justifyContent: "flex-start",
                  }}
                >
                  <IconButton
                    onClick={() => onEditItem(item)}
                    style={{
                      width: "20px",
                      height: "20px",
                      fontSize: "0.7rem",
                    }}
                  >
                    {Icons.Edit}
                  </IconButton>
                  <IconButton
                    onClick={() => handleDeleteItem(item)}
                    style={{
                      width: "20px",
                      height: "20px",
                      fontSize: "0.7rem",
                    }}
                  >
                    {Icons.Delete}
                  </IconButton>
                </div>
              )}
            </TableCell>
          )}
        </TableRow>
      );
    };

    const renderComponentRefRow = (
      refRow: TestTarget & { level?: number }
    ) => {
      const level = refRow.level || 0;
      const comp = resolve_component_ref_target(refRow, allTestTargets);
      const isDangling = !comp;
      const isExpanded = expandedRefIds.has(refRow.id);
      const childRows = isExpanded
        ? build_component_ref_child_rows(refRow, allTestTargets)
        : [];
      const label = comp ? comp.name : refRow.name;

      return (
        <React.Fragment key={`ref-${refRow.id}`}>
          <TableRow style={{ backgroundColor: "#e8f5e9" }}>
            {isBulkDeleteModeItems && <TableCell width="5%" />}
            <TableCell colSpan={7} style={{ whiteSpace: "nowrap" }}>
              <HierarchyContainer level={level}>
                <span
                  onClick={() => {
                    if (!isDangling) toggleRefExpanded(refRow.id);
                  }}
                  style={{
                    cursor: isDangling ? "default" : "pointer",
                    marginRight: 4,
                    userSelect: "none",
                    color: "#0f766e",
                  }}
                  aria-label={isExpanded ? "折りたたむ" : "展開する"}
                >
                  {isDangling ? "•" : isExpanded ? "▼" : "▶"}
                </span>
                <span style={{ marginRight: 4 }}>🔗</span>
                <span style={{ fontWeight: 600, color: "#0f766e" }}>
                  {label}
                </span>
                <span style={{ marginLeft: 4, fontSize: "0.85em", color: "#6b7280" }}>
                  （共通コンポーネント）
                </span>
                {!isDangling && (
                  <IconButton
                    onClick={() => comp && onOpenReferenceTarget?.(comp)}
                    title="参照先コンポーネントを開く"
                    style={{
                      width: "20px",
                      height: "20px",
                      fontSize: "0.7rem",
                      marginLeft: 6,
                    }}
                  >
                    <VisibilityIcon style={{ fontSize: "0.9rem" }} />
                  </IconButton>
                )}
                {isDangling && (
                  <span style={{ marginLeft: 8, fontSize: "0.6rem", color: "#dc2626" }}>
                    参照先が見つかりません
                  </span>
                )}
              </HierarchyContainer>
            </TableCell>
            <TableCell width={`${columnWidths.actions}%`}>
              {/* 「参照先を開く」は名前セルへ移動。操作列は通常行と同じ 編集＋削除の2個に揃え、列幅に収める */}
              <div style={{ display: "flex", gap: "1px", justifyContent: "flex-start" }}>
                <IconButton
                  onClick={() => onEditItem(refRow)}
                  title="参照を編集"
                  style={{ width: "20px", height: "20px", fontSize: "0.7rem" }}
                >
                  {Icons.Edit}
                </IconButton>
                <IconButton
                  onClick={() => onDeleteItem?.(refRow.id)}
                  title="この配置を削除"
                  style={{ width: "20px", height: "20px", fontSize: "0.7rem" }}
                >
                  {Icons.Delete}
                </IconButton>
              </div>
            </TableCell>
          </TableRow>
          {isExpanded &&
            childRows.map((child) =>
              renderItemRow(
                { ...child, componentChainDepth: level + 1 },
                "component",
                `ref-${refRow.id}-child-${child.id}`,
                true
              )
            )}
        </React.Fragment>
      );
    };

    return (
      <>
        {componentTableDisplayRows.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <span style={{ fontWeight: 600, color: "#134e4a" }}>
                共通コンポーネントの項目
              </span>
            </div>
            <TableWrapper data-table-wrapper>
              <Table>
                <TableHead>{renderTableHeaderRow("component")}</TableHead>
                <TableBody>
                  {componentTableDisplayRows.map((row) =>
                    row.rowKind === "component_header"
                      ? renderComponentHeaderRow(row)
                      : renderItemRow(row.panelRow, "component", row.rowKey)
                  )}
                </TableBody>
              </Table>
            </TableWrapper>
          </div>
        )}
        {(() => {
          const renderRows = viewTarget
            ? build_view_render_rows(viewTarget.id, allTestTargets)
            : [];
          return (items.length > 0 || renderRows.length > 0) ? (
            <div>
              {componentTableDisplayRows.length > 0 && (
                <div
                  style={{
                    marginBottom: 8,
                    fontWeight: 600,
                    color: "#334155",
                  }}
                >
                  ビュー直下の項目
                </div>
              )}
              <TableWrapper data-table-wrapper>
                <Table>
                  <TableHead>{renderTableHeaderRow("view")}</TableHead>
                  <TableBody>
                    {renderRows.map((row) =>
                      row.type === "component-ref"
                        ? renderComponentRefRow(row)
                        : renderItemRow(row, "view")
                    )}
                  </TableBody>
                </Table>
              </TableWrapper>
            </div>
          ) : null;
        })()}
      </>
    );
  };
  return (
    <PanelContainer isOpen={isOpen} width={panelWidth} isResizing={isResizing}>
      <ResizeHandle isResizing={isResizing} onMouseDown={handleMouseDown} />
      <PanelContent width={panelWidth}>
        <PanelHeader>
          <HeaderLeft>
            {canGoBackToPreviousTarget && (
              <button
                onClick={() => onBackToPreviousTarget?.()}
                title="元の画面に戻る"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginRight: 8,
                  padding: "2px 8px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 4,
                  background: "#f8fafc",
                  color: "#0f766e",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                ← 元の画面に戻る
              </button>
            )}
            <span className="header-icon">{Icons.List}</span>
            <Title>
              {viewTarget?.name}
              <br />
              <span style={{ fontSize: "0.9rem", color: "#64748b" }}>
                項目仕様書
              </span>
            </Title>
          </HeaderLeft>
          <HeaderRight>
            <IconButton
              onClick={handleDeleteView}
              title={`この${spec_panel_label}を削除`}
              style={{
                marginRight: "8px",
              }}
            >
              {Icons.Delete}
            </IconButton>
            <CloseButton onClick={onClose} title="閉じる">
              {Icons.Close}
            </CloseButton>
          </HeaderRight>
        </PanelHeader>

        <PanelBody>
          <TabContainer>
            <TabList>
              <TabButton
                $isActive={activeTab === "view"}
                onClick={() => {
                  console.log("Switching to view tab");
                  setActiveTab("view");
                }}
              >
                <VisibilityIcon
                  style={{ fontSize: "1rem", marginRight: "6px" }}
                />
                {spec_panel_label}情報
              </TabButton>
              <TabButton
                $isActive={activeTab === "items"}
                onClick={() => {
                  console.log("Switching to items tab");
                  console.log("Current items:", items);
                  setActiveTab("items");
                }}
              >
                <ListAltIcon style={{ fontSize: "1rem", marginRight: "6px" }} />
                項目一覧 ({itemTabTotalCount})
              </TabButton>
              <TabButton
                $isActive={activeTab === "usecases"}
                onClick={() => setActiveTab("usecases")}
              >
                <PlayCircleOutlineIcon
                  style={{ fontSize: "1rem", marginRight: "6px" }}
                />
                機能 ({featureList.length})
              </TabButton>
            </TabList>

            <TabContent>
              <TabBody>
                {(() => {
                  console.log("Rendering tab content, activeTab:", activeTab);

                  if (activeTab === "view") {
                    return (
                      <ViewDisplay
                        viewTarget={viewTarget}
                        isEditing={isEditingView}
                        isSaving={isSavingView}
                        allTestTargets={allTestTargets || []}
                        editedData={editedViewData}
                        onDataChange={setEditedViewData}
                      />
                    );
                  } else if (activeTab === "items") {
                    console.log("Items tab is active");
                    console.log("Items length:", items.length);
                    console.log("Items content:", items);

                    const viewRenderRows = viewTarget
                      ? build_view_render_rows(viewTarget.id, allTestTargets)
                      : [];
                    if (itemTabTotalCount === 0 && viewRenderRows.length === 0) {
                      console.log("Showing empty state");
                      return (
                        <EmptyState>
                          <EmptyTitle>項目がまだ定義されていません</EmptyTitle>
                          <EmptyText>
                            この画面の項目を追加して、画面仕様書を作成しましょう
                          </EmptyText>
                        </EmptyState>
                      );
                    } else {
                      console.log("Calling renderSimpleTableView");
                      return renderSimpleTableView();
                    }
                  } else {
                    // 機能(feature)タブ（read-only 表示 ＋ 作成）
                    return (
                      <UseCaseDisplay
                        viewTarget={viewTarget}
                        features={featureList}
                        isLoading={isLoadingFeatures}
                        testcaseMappings={testcaseMappings}
                        onCreateFeature={
                          apiClient &&
                          workspaceId &&
                          projectId &&
                          viewTarget &&
                          viewTarget.type !== "component"
                            ? openCreateUsecase
                            : undefined
                        }
                        onEditFeature={
                          apiClient &&
                          workspaceId &&
                          projectId &&
                          viewTarget &&
                          viewTarget.type !== "component"
                            ? openEditUsecase
                            : undefined
                        }
                        onViewFeature={
                          viewTarget && viewTarget.type !== "component"
                            ? openViewUsecase
                            : undefined
                        }
                      />
                    );
                  }
                })()}
              </TabBody>

              <TabFooter>
                {activeTab === "view" ? (
                  isEditingView ? (
                    <>
                      <Button
                        variant="secondary"
                        onClick={handleCancelEditView}
                        disabled={isSavingView}
                      >
                        キャンセル
                      </Button>
                      <Button variant="primary" onClick={handleSaveView} disabled={isSavingView}>
                        {isSavingView ? (
                          <>
                            <CircularProgress
                              size={16}
                              color="inherit"
                              style={{ marginRight: 8 }}
                            />
                            保存中...
                          </>
                        ) : (
                          "保存"
                        )}
                      </Button>
                    </>
                  ) : (
                    <Button variant="primary" onClick={handleStartEditView}>
                      {Icons.Edit} 編集
                    </Button>
                  )
                ) : activeTab === "items" ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      gap: "12px",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", gap: "12px" }}>
                      {isBulkDeleteModeItems ? (
                        <Button variant="secondary" onClick={handleCancelBulkDeleteModeItems}>
                          キャンセル
                        </Button>
                      ) : (
                        <>
                          <Button variant="primary" onClick={onAddItem}>
                            {Icons.Add} 項目を追加
                          </Button>
                          <Tooltip
                            title={
                              isProcessingOperationLog
                                ? processingMessage || "操作ログを処理中です..."
                                : !apiClient
                                ? "API設定を有効にしてください"
                                : !workspaceId || !projectId
                                ? "ワークスペースIDとプロジェクトIDを設定してください"
                                : !is_component_panel_target &&
                                  (!viewTarget?.params ||
                                    getViewPaths(viewTarget.params).length === 0)
                                ? `${spec_panel_label}にパスが設定されていません`
                                : `AIを使ってこの${spec_panel_label}配下のテストターゲットを生成します`
                            }
                            arrow
                          >
                            <span>
                              <Button
                                variant="secondary"
                                onClick={handleOperationLogImport}
                                disabled={
                                  !apiClient ||
                                  !workspaceId ||
                                  !projectId ||
                                  isProcessingOperationLog ||
                                  (!is_component_panel_target &&
                                    (!viewTarget?.params ||
                                      getViewPaths(viewTarget.params).length ===
                                        0))
                                }
                              >
                                <AutoAwesomeIcon style={{ fontSize: "1.2rem", marginRight: "4px" }} />
                                AIでターゲットを生成
                              </Button>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </div>
                    <BulkDeleteButton
                      onClick={
                        isBulkDeleteModeItems
                          ? handleBulkDeleteItems
                          : handleStartBulkDeleteModeItems
                      }
                      disabled={
                        (!isBulkDeleteModeItems && items.length === 0) ||
                        (isBulkDeleteModeItems && selectedItemIds.size === 0)
                      }
                    >
                      {isBulkDeleteModeItems
                        ? `${selectedItemIds.size}件を削除`
                        : "一括削除"}
                    </BulkDeleteButton>
                  </div>
                ) : null}
              </TabFooter>
            </TabContent>
          </TabContainer>
        </PanelBody>
      </PanelContent>

      {/* AIでターゲットを生成ダイアログ */}
      {operationLogService && workspaceId && projectId && (
        <>
          <OperationLogImportDialog
            open={showImportDialog}
            onClose={handleImportDialogClose}
            onResultsGenerated={handleResultsGenerated}
            workspaceId={workspaceId}
            projectId={projectId}
            operationLogService={operationLogService}
            // userToken removed - using Auth0 authentication
            viewId={grouping_view_id_for_operation_log}
            viewName={viewTarget?.name}
            scopeDisplayName={viewTarget?.name}
            scopeKind={is_component_panel_target ? "component" : "view"}
            registrationParentTestTargetId={viewTarget?.id}
            mode="target"
          />
          <TestTargetRegistrationDialog
            open={showConfirmDialog}
            onClose={() => setShowConfirmDialog(false)}
            onRegistrationComplete={handleRegistrationComplete}
            targets={generatedTargets}
            existingTargets={allTestTargets}
            workspaceId={workspaceId}
            projectId={projectId}
            operationLogService={operationLogService}
            traceId={generatedTraceId}
            traceparent={generatedTraceparent}
            generationNote={generatedGenerationNote}
            generationNoteLinkUrl={generatedGenerationNoteLinkUrl}
            // userToken removed - using Auth0 authentication
          />
        </>
      )}

      {/* #884: 機能(feature+procedure)の作成・編集は元の UseCaseEditModal を流用。
          パネル祖先の包含ブロックに fixed オーバーレイが閉じ込められないよう body 直下へ portal する
          （元はトップレベル描画で全画面だったため）。 */}
      {usecaseModalOpen &&
        createPortal(
          <UseCaseEditModal
            isOpen={usecaseModalOpen}
            onClose={() => setUsecaseModalOpen(false)}
            useCase={usecaseModalUseCase}
            onSave={handleSaveUsecase}
            testTargets={allTestTargets}
            currentViewId={viewTarget?.id}
            initialMode={usecaseModalMode}
            workspaceId={workspaceId}
            projectId={projectId}
            apiConfig={apiConfig}
            testcaseMappings={testcaseMappings}
            hidePolicyTab
            hideIssuesTab
            entityLabel="機能"
          />,
          document.body
        )}
    </PanelContainer>
  );
};

export default ItemSpecificationPanel;
